import React, { useState, useEffect, useMemo } from 'react';
import { PlusCircle, Loader2, Clock, Car, Receipt, Wallet, AlertTriangle, ArrowUpRight, ArrowDownRight, TrendingUp, Landmark, Trash2, Moon, CalendarDays, Tag, Pencil, Check, X, FileDown, Share2, Plus, Download, RefreshCw } from "lucide-react";
import { RelazioneGiornalieraService, PraticaService, ClientService, PreventiviService } from '../services/apiService';
import { useToast } from './ToastContext';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { parseDecimal } from '../utils/utils';
import { generaProformaPdf, calcolaTotaliProforma } from '../utils/generaProformaPdf';
import {
    calcolaCanali, IvaMode, ALIQUOTA_IVA, IVA_MODE_LABEL, IVA_MODE_SHORT,
    DEFAULT_RETTIFICA_MODE, splitRettifica, parseIvaMode,
} from '../utils/contabilitaCanali';
import { economiaProforma, hashMarkerBaseIva } from '../utils/proformaPayload';
import NumberInput from './NumberInput';
import RettificheEstrattoModal from './RettificheEstrattoModal';
import {
    RettificaEstratto, VoceEstratto, VOCI_ESTRATTO, VOCE_ESTRATTO_LABEL,
    parseRettificheEstratto, sommaRettificheEstratto, formatQuantitaEstratto,
} from '../utils/rettificheEstratto';

// Numeri a video e nelle descrizioni sempre in formato italiano (virgola decimale).
const fmtIt = (v: number, decimali = 2): string =>
    (Number(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: decimali, maximumFractionDigits: decimali });
const fmtKm = (v: number): string => (Number(v) || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });



const calcolaOre = (inizio: string, fine: string): number => {
    if (!inizio || !fine) return 0;
    const [h1, m1] = inizio.split(':').map(Number);
    const [h2, m2] = fine.split(':').map(Number);
    // fine < inizio: l'intervallo supera la mezzanotte (19:00 → 01:00 = 6h).
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff < 0) diff += 24 * 60;
    return Math.max(0, Math.round(diff / 6) / 10);
};

const toMin = (s?: string) => {
    if (!s) return 0;
    const [h, m] = s.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

// Divide le ore di un report in feriali e festive/notturne (stessa logica del BE).
// Nei festivi tutte le ore sono festive; altrimenti solo quelle in [nightStart, nightEnd).
// Se fine < inizio l'intervallo supera la mezzanotte: ogni parte è classificata col proprio giorno.
const classificaOre = (
    inizio?: string,
    fine?: string,
    dataRelazione?: string,
    nightStart: string = '22:00',
    nightEnd: string = '06:00'
): { oreFer: number; oreFestNot: number } => {
    if (!inizio || !fine) return { oreFer: 0, oreFestNot: 0 };
    const i = toMin(inizio);
    const f = toMin(fine);
    if (f === i) return { oreFer: 0, oreFestNot: 0 };
    const ns = toMin(nightStart);
    const ne = toMin(nightEnd);
    const nightMinutes = (a: number, b: number): number => {
        if (b <= a) return 0;
        if (ns < ne) return Math.max(0, Math.min(b, ne) - Math.max(a, ns));
        return Math.max(0, Math.min(b, ne) - Math.max(a, 0))
             + Math.max(0, Math.min(b, 24 * 60) - Math.max(a, ns));
    };
    const giorno0 = dataRelazione ? new Date(dataRelazione + 'T00:00:00') : null;
    const segmento = (a: number, b: number, offsetGiorni: number): { oreFer: number; oreFestNot: number } => {
        const tot = (b - a) / 60;
        if (giorno0 && !isNaN(giorno0.getTime())) {
            const g = new Date(giorno0);
            g.setDate(g.getDate() + offsetGiorni);
            if (isFestivoItaliano(g)) return { oreFer: 0, oreFestNot: tot };
        }
        const notte = nightMinutes(a, b) / 60;
        return { oreFer: tot - notte, oreFestNot: notte };
    };
    if (f < i) {
        const s1 = segmento(i, 24 * 60, 0);
        const s2 = segmento(0, f, 1);
        return { oreFer: s1.oreFer + s2.oreFer, oreFestNot: s1.oreFestNot + s2.oreFestNot };
    }
    return segmento(i, f, 0);
};

// Festività italiane fisse + Pasqua/Pasquetta (algoritmo di Gauss). La domenica è festiva.
const isFestivoItaliano = (d: Date): boolean => {
    if (d.getDay() === 0) return true;
    const m = d.getMonth() + 1, day = d.getDate();
    const fissi: [number, number][] = [
        [1, 1], [1, 6], [4, 25], [5, 1], [6, 2],
        [8, 15], [11, 1], [12, 8], [12, 25], [12, 26],
    ];
    if (fissi.some(([fm, fd]) => fm === m && fd === day)) return true;
    const Y = d.getFullYear();
    const a = Y % 19, b = Math.floor(Y / 100), c = Y % 100;
    const d2 = Math.floor(b / 4), e = b % 4;
    const ff = Math.floor((b + 8) / 25);
    const g = Math.floor((b - ff + 1) / 3);
    const h = (19 * a + b - d2 - g + 15) % 30;
    const ii = Math.floor(c / 4), k = c % 4;
    const L = (32 + 2 * e + 2 * ii - h - k) % 7;
    const mo = Math.floor((a + 11 * h + 22 * L) / 451);
    const month = Math.floor((h + L - 7 * mo + 114) / 31);
    const dayP = ((h + L - 7 * mo + 114) % 31) + 1;
    const isPasqua = d.getMonth() === month - 1 && d.getDate() === dayP;
    const isPasquetta = d.getMonth() === month - 1 && d.getDate() === dayP + 1;
    return isPasqua || isPasquetta;
};

interface Aggiustamento {
    id: string;
    descrizione: string;
    importo: number;
    // Come la rettifica incide sui canali imponibile/IVA.
    ivaMode?: IvaMode;
}

// Riga della proforma, usata dal modale di anteprima e dal PDF.
// inTotal: entra nel totale; isSection: intestazione di sezione; isExtra: aggiunta a mano.
interface ProformaRow {
    id: string;
    descrizione: string;
    quantita: string;
    tariffa: string;
    importo: number;
    inTotal: boolean;
    isSection?: boolean;
    isExtra?: boolean;
    noIva?: boolean;   // nel totale ma esclusa dalla base IVA (spese escluse da IVA)
}

interface Props {
    praticaId: number;
    pratica: any;
    settings: any;
    movimenti: any[];
    onOpenPayment: () => void;
    onDeleteMovimento?: (id: any) => void;
    // Spunta "Fatturato" sul singolo movimento: il BE lo conta come fatturato
    // e lo toglie dalla fatturazione in sospeso.
    onToggleFatturato?: (id: any, fatturato: boolean) => void;
    // Dopo un salvataggio su datiAggiuntiviJson il parent aggiorna la pratica,
    // così i valori restano visibili cambiando tab.
    onDatiAggiuntiviChange?: (patch: Record<string, any>) => void;
}

export const TabContabilita: React.FC<Props> = ({ praticaId, pratica, settings, movimenti, onOpenPayment, onDeleteMovimento, onToggleFatturato, onDatiAggiuntiviChange }) => {
    const { addToast } = useToast();
    const navigate = useNavigate();
    const [reports, setReports] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    // Preventivi collegati al mandato, caricati una volta al mount.
    const [preventiviCollegati, setPreventiviCollegati] = useState<any[]>([]);
    // Preventivi/proforma pendenti dello stesso cliente, collegabili a mano.
    // Si ricaricano insieme ai collegati dopo ogni link/unlink.
    const [preventiviPendenti, setPreventiviPendenti] = useState<any[]>([]);
    const clientIdPratica = pratica?.client?.id ?? pratica?.clientId ?? null;
    const reloadPreventivi = React.useCallback(async () => {
        if (!praticaId) return;
        try {
            const res = await PreventiviService.getByPratica(praticaId);
            setPreventiviCollegati(res?.data || []);
        } catch { /* widget facoltativo */ }
        if (clientIdPratica) {
            try {
                const res = await PreventiviService.getPendingByClient(clientIdPratica);
                setPreventiviPendenti(res?.data || []);
            } catch { /* widget facoltativo */ }
        }
    }, [praticaId, clientIdPratica]);
    useEffect(() => { reloadPreventivi(); }, [reloadPreventivi]);

    const linkPreventivo = async (prevId: number | string) => {
        try {
            await PreventiviService.linkToPratica(prevId, Number(praticaId));
            await reloadPreventivi();
            addToast('Preventivo collegato al mandato', 'success');
        } catch (e: any) {
            addToast('Errore nel collegamento: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };
    const unlinkPreventivo = async (prevId: number | string) => {
        if (!window.confirm('Scollegare questo documento dal mandato? Tornerà disponibile per altri collegamenti.')) return;
        try {
            await PreventiviService.linkToPratica(prevId, null);
            await reloadPreventivi();
            addToast('Documento scollegato', 'success');
        } catch (e: any) {
            addToast('Errore nello scollegamento: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };

    // Elimina una proforma emessa (soft-delete). Chiede conferma perché irreversibile.
    const deleteProforma = async (prevId: number | string) => {
        if (!window.confirm('Eliminare definitivamente questa proforma? Il PDF resterà comunque archiviato su S3 ma non sarà più visibile qui.')) return;
        try {
            await PreventiviService.delete(prevId);
            await reloadPreventivi();
            addToast('Proforma eliminata', 'success');
        } catch (e: any) {
            addToast('Errore eliminazione: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };

    // Override locale del compenso finale: null = leggi da datiAggiuntiviJson.
    // Dopo il salvataggio tiene il nuovo valore senza attendere il refresh del parent.
    const [compensoFinaleLocal, setCompensoFinaleLocal] = useState<number | null>(null);
    const [editingCompensoFinale, setEditingCompensoFinale] = useState(false);

    // Note libere della contabilità con autosave (debounce 800ms).
    const [noteContabilita, setNoteContabilita] = useState<string>('');
    const [savingNote, setSavingNote] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    useEffect(() => {
        setNoteContabilita(pratica?.noteContabilita || '');
    }, [pratica?.id, pratica?.noteContabilita]);

    useEffect(() => {
        if (!praticaId) return;
        // Al primo mount non serve salvare il valore iniziale.
        if ((noteContabilita || '') === (pratica?.noteContabilita || '')) return;
        setSavingNote('saving');
        const t = setTimeout(async () => {
            try {
                await PraticaService.patchNoteContabilita(praticaId, noteContabilita || '');
                setSavingNote('saved');
                // Reset visuale "Salvato ✓" dopo 1.5s
                setTimeout(() => setSavingNote('idle'), 1500);
            } catch {
                setSavingNote('error');
            }
        }, 800);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noteContabilita, praticaId]);
    const [compensoFinaleInput, setCompensoFinaleInput] = useState<string>('');
    const [savingCompensoFinale, setSavingCompensoFinale] = useState(false);
    const [generatingProforma, setGeneratingProforma] = useState(false);
    // Anteprima proforma: le righe si possono modificare prima di generare il PDF.
    const [proformaPreviewOpen, setProformaPreviewOpen] = useState(false);
    const [proformaMode, setProformaMode] = useState<'completo' | 'saldo'>('completo');
    // "Non mostrare IVA": il box totali mostra solo l'imponibile.
    const [proformaNascondiIva, setProformaNascondiIva] = useState(false);
    // Se false la proforma omette pagamenti, saldo e acconto.
    const [proformaMostraPagamenti, setProformaMostraPagamenti] = useState(true);
    const [proformaRows, setProformaRows] = useState<ProformaRow[]>([]);
    const [newRowDraft, setNewRowDraft] = useState<{ descrizione: string; quantita: string; tariffa: string; importo: string }>({ descrizione: '', quantita: '', tariffa: '', importo: '' });
    // Condivisione PDF via Web Share API.
    const [isPreparingShare, setIsPreparingShare] = useState(false);

    const [aggiustamentiLocal, setAggiustamentiLocal] = useState<Aggiustamento[] | null>(null);
    // "Spese escluse da IVA": null = usa il valore della pratica.
    const [speseEscluseIvaLocal, setSpeseEscluseIvaLocal] = useState<boolean | null>(null);
    const [savingSpeseEscluseIva, setSavingSpeseEscluseIva] = useState(false);
    const [aggiustamentiModalOpen, setAggiustamentiModalOpen] = useState(false);
    const [aggiustamentiEdit, setAggiustamentiEdit] = useState<Aggiustamento[]>([]);
    const [savingAggiustamenti, setSavingAggiustamenti] = useState(false);

    // Rettifiche dell'estratto conto operativo (solo a consuntivo): null = leggi dalla pratica.
    const [rettificheEstrattoLocal, setRettificheEstrattoLocal] = useState<RettificaEstratto[] | null>(null);
    const [rettificheEstrattoModalOpen, setRettificheEstrattoModalOpen] = useState(false);
    const [savingRettificheEstratto, setSavingRettificheEstratto] = useState(false);

    // Reset override quando cambia la pratica
    useEffect(() => { setCompensoFinaleLocal(null); setAggiustamentiLocal(null); setRettificheEstrattoLocal(null); }, [praticaId]);

    useEffect(() => {
        const fetchDati = async () => {
            setLoading(true);
            try {
                const res = await RelazioneGiornalieraService.getByPratica(praticaId);
                setReports(Array.isArray(res) ? res : res.data || []);
            } catch (e) {
                console.error("Errore caricamento report in contabilità", e);
            } finally {
                setLoading(false);
            }
        };
        if (praticaId) fetchDati();
    }, [praticaId]);

    // Dati della pratica
    const { preventivo, accontoIniziale, tariffaOraria, tariffaFestivaNotturna, tariffaGiornaliera, tariffaKm, resolvedModelId, isConsuntivo, compensoFinaleFromPratica, aggiustamentiFromPratica, speseEscluseIvaFromPratica, rettificheEstrattoFromPratica } = useMemo(() => {
        let extra: any = {};
        if (pratica?.datiAggiuntiviJson) {
            try { extra = typeof pratica.datiAggiuntiviJson === 'string' ? JSON.parse(pratica.datiAggiuntiviJson) : pratica.datiAggiuntiviJson; } catch (e) {}
        }
        // parseFloat(0) || fallback scarterebbe uno 0 esplicito (es. niente rimborso km):
        // pickValore usa il valore della pratica se presente, anche 0, altrimenti il default.
        const pickValore = (rawPratica: any, fallback: any): number => {
            if (rawPratica !== undefined && rawPratica !== null && rawPratica !== '') {
                const n = parseFloat(rawPratica);
                if (!isNaN(n)) return n;
            }
            const f = parseFloat(fallback);
            return isNaN(f) ? 0 : f;
        };

        // Bonifica e consulenza sono sempre a corpo: il wizard non mostra le condizioni
        // economiche e tipo_compenso resterebbe 'consuntivo'. Le forziamo a corpo come il BE.
        const resolvedModelIdLocal = pratica?.modelId || extra.selectedModelId || '';
        const isBonificaOConsulenza = resolvedModelIdLocal === 'prev-bonifiche' || resolvedModelIdLocal === 'prev-consulenza';

        return {
            preventivo: parseFloat(extra.importo_preventivato) || parseFloat(extra.compenso_concordato) || 0,
            accontoIniziale: parseFloat(extra.acconto) || parseFloat(extra.acconto_compenso) || 0,
            tariffaOraria: pickValore(extra.tariffa_oraria_feriale, settings?.hourlyRate),
            tariffaFestivaNotturna: pickValore(extra.tariffa_oraria_festiva, 0),
            tariffaGiornaliera: pickValore(extra.tariffa_giornaliera, 0),
            tariffaKm: pickValore(extra.rimborso_km, settings?.priceKM),
            resolvedModelId: resolvedModelIdLocal,
            isConsuntivo: extra.tipo_compenso === 'consuntivo' && !isBonificaOConsulenza,
            compensoFinaleFromPratica: parseFloat(extra.compenso_finale_concordato) || 0,
            aggiustamentiFromPratica: Array.isArray(extra.aggiustamenti) ? (extra.aggiustamenti as Aggiustamento[]) : [],
            // Assente/null = true; solo false disattiva l'esclusione.
            speseEscluseIvaFromPratica: !(extra.spese_escluse_iva === false || extra.spese_escluse_iva === 'false'),
            rettificheEstrattoFromPratica: parseRettificheEstratto(extra.rettifiche_estratto),
        };
    }, [pratica, settings]);

    // Valgono solo a consuntivo, come nel BE (a corpo l'estratto conto non incide).
    const rettificheEstratto = rettificheEstrattoLocal !== null ? rettificheEstrattoLocal : rettificheEstrattoFromPratica;
    const hasRettificheEstratto = isConsuntivo && rettificheEstratto.length > 0;
    const sommeEstratto = useMemo(
        () => sommaRettificheEstratto(isConsuntivo ? rettificheEstratto : []),
        [rettificheEstratto, isConsuntivo]);

    // Costi a consuntivo:
    // 1. tariffa giornaliera × giorni lavorati
    // 2. ore feriali × tariffa feriale
    // 3. ore notturne (default 22:00-06:00) o festive × tariffa festiva/notturna
    // 4. + km × tariffa km + spese approvate
    const stats = useMemo(() => {
        const approvati = reports.filter(r => r.stato === 'APPROVATO');
        const nightStart = settings?.orarioNotturnoInizio || '22:00';
        const nightEnd = settings?.orarioNotturnoFine || '06:00';

        let totOreFeriali = 0;
        let totOreFestivaNotturna = 0;
        // Una giornata per data distinta, non per report (come nel BE).
        const dateLavorate = new Set<string>();
        let totKm = 0;
        let totSpese = 0;
        let totBuoniPasto = 0;
        // Le spese con carta aziendale restano fuori dalla base (come nel BE); il titolare può
        // addebitarle al cliente come rettifica. Si usa s.importo perché importoApprovato è 0.
        let totSpeseCarta = 0;

        approvati.forEach(r => {
            // --- giornaliera: 1 data distinta = 1 giornata ---
            if (r.dataRelazione) dateLavorate.add(String(r.dataRelazione).slice(0, 10));

            // Ore feriali / festive-notturne per giorno, anche oltre la mezzanotte (vedi classificaOre)
            const cls = classificaOre(r.orarioInizioIndagine, r.orarioFineIndagine, r.dataRelazione, nightStart, nightEnd);
            let oreFer = cls.oreFer;
            let oreFestNot = cls.oreFestNot;

            // se l'admin ha approvato un totale ore diverso, scala proporzionalmente
            if (r.oreFatturabiliApprovate != null) {
                const totaleOreCalc = oreFer + oreFestNot;
                const oreApprovate = Number(r.oreFatturabiliApprovate);
                if (totaleOreCalc > 0) {
                    const ratio = oreApprovate / totaleOreCalc;
                    oreFer *= ratio;
                    oreFestNot *= ratio;
                } else {
                    oreFer = oreApprovate;
                }
            }

            totOreFeriali += oreFer;
            totOreFestivaNotturna += oreFestNot;

            totKm += r.kmApprovati != null ? r.kmApprovati : (r.kmPercorsi || r.km || 0);

            // I buoni pasto sono un benefit al collaboratore, non spese a rivalsa del cliente.
            const speseList = r.speseEspanso || r.spese || [];
            speseList.forEach((s: any) => {
                if (s.buonoPastoAuto) {
                    // Sommati a parte solo per mostrarli, mai addebitati al cliente.
                    totBuoniPasto += s.importoApprovato != null ? s.importoApprovato : (s.importo || 0);
                    return;
                }
                // Le spese con carta aziendale non sono rivalsa cliente (come nel BE).
                if (s.anticipatoAzienda) { totSpeseCarta += Number(s.importo) || 0; return; }
                const imp = s.importoApprovato != null ? s.importoApprovato : (s.importo || 0);
                totSpese += imp;
            });
        });

        // Quantità dai report, poi le rettifiche dell'estratto conto: ogni voce resta >= 0.
        const daReport: Record<VoceEstratto, number> = {
            ORE_FERIALI: totOreFeriali, ORE_FESTIVE: totOreFestivaNotturna, KM: totKm, SPESE: totSpese,
        };
        totOreFeriali = Math.max(0, totOreFeriali + sommeEstratto.ORE_FERIALI);
        totOreFestivaNotturna = Math.max(0, totOreFestivaNotturna + sommeEstratto.ORE_FESTIVE);
        totKm = Math.max(0, totKm + sommeEstratto.KM);
        totSpese = Math.max(0, totSpese + sommeEstratto.SPESE);

        const costoOreFeriali = totOreFeriali * tariffaOraria;
        const costoOreFestivaNotturna = totOreFestivaNotturna * tariffaFestivaNotturna;
        const giorniLavorati = dateLavorate.size;
        const costoGiornaliera = giorniLavorati * tariffaGiornaliera;
        const costoKm = totKm * tariffaKm;
        const totOre = totOreFeriali + totOreFestivaNotturna;
        const costoOre = costoOreFeriali + costoOreFestivaNotturna;
        const totaleConsuntivo = costoOre + costoGiornaliera + costoKm + totSpese;

        return {
            totOre,
            totOreFeriali,
            totOreFestivaNotturna,
            giorniLavorati,
            totKm,
            totSpese,
            totSpeseCarta,
            totBuoniPasto,
            costoOre,
            costoOreFeriali,
            costoOreFestivaNotturna,
            costoGiornaliera,
            costoKm,
            totaleConsuntivo,
            daReport,
            reportDaApprovare: reports.filter(r => r.stato !== 'APPROVATO').length
        };
    }, [reports, tariffaOraria, tariffaFestivaNotturna, tariffaGiornaliera, tariffaKm, settings, sommeEstratto]);

    const isConsulenzaOBonifica = resolvedModelId === 'prev-bonifiche' || resolvedModelId === 'prev-consulenza';
    console.log('[TabContabilita] resolvedModelId:', resolvedModelId, '| isConsulenzaOBonifica:', isConsulenzaOBonifica, '| pratica.modelId:', pratica?.modelId);

    // Movimenti e saldi.
    // L'acconto pattuito non è un incasso finché non viene registrato come entrata.
    const totaleIncassi = movimenti.filter(m => m.tipo === 'entrata').reduce((acc, m) => acc + Number(m.importo), 0);
    const totaleUsciteExtra = movimenti.filter(m => m.tipo === 'uscita').reduce((acc, m) => acc + Number(m.importo), 0);

    const costoGenerale = stats.totaleConsuntivo + totaleUsciteExtra;

    // Base di fatturazione:
    // - compenso finale concordato, se impostato, vince su tutto
    // - a corpo: importo fisso pattuito
    // - a consuntivo: maturato finora (ore + km + spese approvate)
    // L'acconto pattuito fa comunque da minimo.
    const baseRaw = isConsuntivo ? stats.totaleConsuntivo : preventivo;
    const baseConsuntivoOPreventivo = Math.max(baseRaw, accontoIniziale);

    // Rettifiche +/- sul compenso base
    const aggiustamenti = aggiustamentiLocal !== null ? aggiustamentiLocal : aggiustamentiFromPratica;
    const hasAggiustamenti = aggiustamenti.length > 0;
    const sumAggiustamenti = aggiustamenti.reduce((s, a) => s + Number(a.importo || 0), 0);

    // Formato nuovo: base + rettifiche; formato vecchio: override fisso
    const compensoFinale = hasAggiustamenti
        ? baseConsuntivoOPreventivo + sumAggiustamenti
        : (compensoFinaleLocal !== null ? compensoFinaleLocal : compensoFinaleFromPratica);
    const hasCompensoFinale = hasAggiustamenti || compensoFinale > 0;

    // Canali imponibile e IVA indipendenti. imponibileBase è il compenso prima delle
    // rettifiche, che calcolaCanali somma per canale secondo il loro ivaMode.
    const imponibileBase = hasAggiustamenti
        ? baseConsuntivoOPreventivo
        : (compensoFinale > 0 ? compensoFinale : baseConsuntivoOPreventivo);
    // A consuntivo le spese a rivalsa stanno nella base: restano imponibili ma senza IVA
    // (imponibileBaseEsente), salvo compenso finale flat. A corpo l'esente è 0: le spese
    // entrano come rettifica SOLO_IMPONIBILE.
    const speseEscluseIva = speseEscluseIvaLocal !== null ? speseEscluseIvaLocal : speseEscluseIvaFromPratica;
    const baseDaConsuntivo = hasAggiustamenti || !(compensoFinale > 0);
    const imponibileBaseEsente = (isConsuntivo && speseEscluseIva && baseDaConsuntivo)
        ? Math.max(0, Math.min(stats.totSpese, imponibileBase))
        : 0;
    const canali = calcolaCanali({ imponibileBase, imponibileBaseEsente, aggiustamenti, movimenti });
    const defaultModeSpese: IvaMode = speseEscluseIva ? 'SOLO_IMPONIBILE' : DEFAULT_RETTIFICA_MODE;

    const handleToggleSpeseEscluseIva = async (val: boolean) => {
        if (savingSpeseEscluseIva) return;
        setSavingSpeseEscluseIva(true);
        try {
            await PraticaService.patchDatiAggiuntivi(praticaId, { spese_escluse_iva: val });
            setSpeseEscluseIvaLocal(val);
            onDatiAggiuntiviChange?.({ spese_escluse_iva: val });
            addToast(val ? 'Spese escluse da IVA' : 'Spese soggette a IVA', 'success');
        } catch {
            addToast('Errore nel salvataggio', 'error');
        } finally {
            setSavingSpeseEscluseIva(false);
        }
    };
    const baseFatturazione = canali.imponibileDovuto;
    // Quota delle rettifiche sul canale imponibile: "solo IVA" non conta,
    // "IVA inclusa" va scorporata. Per la somma grezza vedi sumAggiustamenti.
    const sumAggImponibile = Math.round((canali.imponibileDovuto - baseConsuntivoOPreventivo) * 100) / 100;
    const sumAggIva = Math.round((canali.ivaDovuta - canali.ivaBase) * 100) / 100;

    // Visibile solo se ci sono spese a rivalsa o con carta aziendale.
    const speseEscluseIvaToggle = (stats.totSpese > 0.005 || stats.totSpeseCarta > 0.005) ? (
        <label className="mt-3 flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
                type="checkbox"
                className="mt-0.5 accent-blue-600"
                checked={speseEscluseIva}
                disabled={savingSpeseEscluseIva}
                onChange={e => handleToggleSpeseEscluseIva(e.target.checked)}
            />
            <span>
                <span className="font-semibold text-slate-700">Spese escluse da IVA (rimborsi documentati)</span>
                {savingSpeseEscluseIva && <Loader2 size={11} className="inline ml-1 animate-spin"/>}
                <span className="block text-[10px] text-slate-400">
                    {isConsuntivo
                        ? "Le spese a rivalsa restano nell'imponibile ma non generano IVA; vale anche per le nuove rettifiche \"carta aziendale\"."
                        : 'Le nuove rettifiche "Spese a rivalsa" / "carta aziendale" vengono create in modalità "Solo imponibile" (senza IVA).'}
                </span>
            </span>
        </label>
    ) : null;

    const handleSaveCompensoFinale = async (val: number) => {
        setSavingCompensoFinale(true);
        try {
            await PraticaService.patchDatiAggiuntivi(praticaId, { compenso_finale_concordato: val });
            setCompensoFinaleLocal(val);
            onDatiAggiuntiviChange?.({ compenso_finale_concordato: val });
            addToast(val > 0 ? 'Importo finale impostato' : 'Importo finale rimosso', 'success');
            setEditingCompensoFinale(false);
            setCompensoFinaleInput('');
        } catch (e) {
            console.error(e);
            addToast('Errore nel salvataggio', 'error');
        } finally {
            setSavingCompensoFinale(false);
        }
    };

    const openAggiustamentiModal = () => {
        let initial: Aggiustamento[] = [...aggiustamenti];
        // Conversione dal vecchio formato flat
        if (initial.length === 0 && compensoFinaleFromPratica > 0) {
            const diff = compensoFinaleFromPratica - baseConsuntivoOPreventivo;
            if (diff !== 0) {
                initial = [{ id: Date.now().toString(), descrizione: 'Importo concordato', importo: diff, ivaMode: DEFAULT_RETTIFICA_MODE }];
            }
        }
        setAggiustamentiEdit(initial);
        setAggiustamentiModalOpen(true);
    };

    const handleSaveAggiustamenti = async () => {
        setSavingAggiustamenti(true);
        try {
            await PraticaService.patchDatiAggiuntivi(praticaId, {
                aggiustamenti: aggiustamentiEdit,
                compenso_finale_concordato: 0,
            });
            setAggiustamentiLocal([...aggiustamentiEdit]);
            onDatiAggiuntiviChange?.({ aggiustamenti: aggiustamentiEdit, compenso_finale_concordato: 0 });
            addToast(aggiustamentiEdit.length > 0 ? 'Rettifiche salvate' : 'Rettifiche rimosse', 'success');
            setAggiustamentiModalOpen(false);
        } catch {
            addToast('Errore nel salvataggio', 'error');
        } finally {
            setSavingAggiustamenti(false);
        }
    };

    const handleSaveRettificheEstratto = async (list: RettificaEstratto[]) => {
        setSavingRettificheEstratto(true);
        try {
            await PraticaService.patchDatiAggiuntivi(praticaId, { rettifiche_estratto: list });
            setRettificheEstrattoLocal(list);
            onDatiAggiuntiviChange?.({ rettifiche_estratto: list });
            addToast(list.length > 0 ? 'Rettifiche estratto conto salvate' : 'Rettifiche estratto conto rimosse', 'success');
            setRettificheEstrattoModalOpen(false);
        } catch {
            addToast('Errore nel salvataggio', 'error');
        } finally {
            setSavingRettificheEstratto(false);
        }
    };

    // Sotto la quantità dell'estratto conto: valore dai report e rettifica.
    const dettaglioRettifica = (v: VoceEstratto) => {
        const q = sommeEstratto[v];
        if (!(Math.abs(q) > 0)) return null;
        return (
            <div className="text-[10px] font-semibold text-blue-600 mt-0.5 leading-tight">
                {formatQuantitaEstratto(v, stats.daReport[v], false)} dai report
                <br/>{VOCE_ESTRATTO_LABEL[v]} {formatQuantitaEstratto(v, q)}
            </div>
        );
    };

    // Aliquota IVA fissa al 22%: il BE ignora il valore in Settings.
    // Gli importi del mandato sono imponibili, il cliente paga l'ivato.
    const aliquotaIva = ALIQUOTA_IVA;
    const baseFatturazioneIvata = canali.totaleDovuto;
    const ivaValore = canali.ivaDovuta;
    const saldoDaIncassare = canali.residuoTotale;
    const percIncassato = canali.percIncassato;
    const isSaldato = canali.isSaldato;
    // Residuo per canale: un canale può essere saldato e l'altro no, o l'IVA può essere in eccesso.
    const saldoDaIncassareImponibile = canali.residuoImponibile;
    const saldoDaIncassareIva = canali.residuoIva;

    // ── Righe proforma ──
    // Righe di default calcolate dalla pratica, modificabili nell'anteprima prima del PDF.
    const formatEur = (v: number) => `€ ${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const buildProformaRows = (): ProformaRow[] => {
        const rows: ProformaRow[] = [];
        const mkId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const aggiungiBase = () => {
            const before = rows.reduce((s, r) => s + (r.inTotal ? Number(r.importo) || 0 : 0), 0);
            if (isConsuntivo) {
                if (stats.totOreFeriali > 0) rows.push({ id: mkId(), descrizione: 'Prestazioni in fascia feriale', quantita: `${fmtIt(stats.totOreFeriali, 1)} h`, tariffa: formatEur(tariffaOraria) + '/h', importo: stats.costoOreFeriali, inTotal: true });
                if (stats.totOreFestivaNotturna > 0 && tariffaFestivaNotturna > 0) rows.push({ id: mkId(), descrizione: 'Prestazioni in fascia festiva/notturna', quantita: `${fmtIt(stats.totOreFestivaNotturna, 1)} h`, tariffa: formatEur(tariffaFestivaNotturna) + '/h', importo: stats.costoOreFestivaNotturna, inTotal: true });
                if (stats.giorniLavorati > 0 && tariffaGiornaliera > 0) rows.push({ id: mkId(), descrizione: 'Tariffa giornaliera', quantita: `${stats.giorniLavorati} g`, tariffa: formatEur(tariffaGiornaliera) + '/g', importo: stats.costoGiornaliera, inTotal: true });
                if (stats.totKm > 0) rows.push({ id: mkId(), descrizione: 'Indennità chilometrica', quantita: `${fmtKm(stats.totKm)} km`, tariffa: formatEur(tariffaKm) + '/km', importo: stats.costoKm, inTotal: true });
                // Spese a rivalsa approvate: fanno parte del consuntivo, una sola riga col totale.
                if (stats.totSpese > 0.005) rows.push({ id: mkId(), descrizione: 'Rimborso spese sostenute', quantita: '—', tariffa: '—', importo: stats.totSpese, inTotal: true, ...(speseEscluseIva ? { noIva: true } : {}) });
                if (rows.length === 0) rows.push({ id: mkId(), descrizione: 'Prestazioni investigative (consuntivo in maturazione)', quantita: '—', tariffa: '—', importo: 0, inTotal: true });
            } else {
                rows.push({ id: mkId(), descrizione: 'Compenso pattuito a corpo per le prestazioni di mandato', quantita: '—', tariffa: '—', importo: preventivo, inTotal: true });
            }
            // Minimo acconto: se l'acconto supera la base grezza si aggiunge la differenza,
            // così la somma delle righe coincide con l'imponibile del gestionale.
            const pushed = rows.reduce((s, r) => s + (r.inTotal ? Number(r.importo) || 0 : 0), 0) - before;
            const floor = Math.round((baseConsuntivoOPreventivo - pushed) * 100) / 100;
            if (floor > 0.005) {
                rows.push({ id: mkId(), descrizione: 'Compenso minimo pattuito (acconto)', quantita: '—', tariffa: '—', importo: floor, inTotal: true });
            }
        };

        if (hasAggiustamenti) {
            aggiungiBase();
            rows.push({ id: mkId(), descrizione: 'RETTIFICHE AL COMPENSO', quantita: '', tariffa: '', importo: 0, inTotal: false, isSection: true });
            aggiustamenti.forEach(a => {
                const pos = Number(a.importo) >= 0;
                // Divisione a due canali come in calcolaCanali: l'imponibile va nel totale righe,
                // le rettifiche "Solo IVA" compaiono solo come riga informativa e la loro IVA va in ivaExtra.
                // Usare il meno ASCII "-": il font WinAnsi di jsPDF non ha U+2212.
                const mode = parseIvaMode(a.ivaMode, DEFAULT_RETTIFICA_MODE);
                const s = splitRettifica(Number(a.importo) || 0, mode);
                if (mode === 'SOLO_IVA') {
                    rows.push({
                        id: mkId(),
                        descrizione: `  ${pos ? '+' : '-'} ${a.descrizione || 'Rettifica'} (solo IVA)`,
                        quantita: '—', tariffa: '—',
                        importo: s.iva,
                        inTotal: false,
                    });
                } else {
                    // "Solo imponibile": nel totale ma fuori dalla base IVA.
                    rows.push({
                        id: mkId(),
                        descrizione: `  ${pos ? '+' : '-'} ${a.descrizione || 'Rettifica'}`,
                        quantita: '—', tariffa: '—',
                        importo: s.imp,
                        inTotal: true,
                        ...(mode === 'SOLO_IMPONIBILE' ? { noIva: true } : {}),
                    });
                }
            });
        } else if (hasCompensoFinale) {
            rows.push({ id: mkId(), descrizione: 'Compenso finale concordato (a saldo)', quantita: '—', tariffa: '—', importo: compensoFinale, inTotal: true });
        } else {
            aggiungiBase();
        }

        // A corpo le spese a rivalsa non si aggiungono da sole: il titolare le inserisce
        // con "Aggiungi a rettifiche". A consuntivo sono già nella base. Niente doppi conteggi.

        // Le uscite extra manuali restano separate dalle spese da scontrino.
        const usciteExtra = movimenti.filter(m => m.tipo === 'uscita');
        if (usciteExtra.length > 0) {
            rows.push({ id: mkId(), descrizione: 'USCITE EXTRA', quantita: '', tariffa: '', importo: 0, inTotal: false, isSection: true });
            usciteExtra.forEach((m: any) => {
                const label = m.descrizione || 'Uscita';
                const dataStr = m.data ? new Date(m.data).toLocaleDateString('it-IT') : '—';
                rows.push({ id: mkId(), descrizione: `  ${label}`, quantita: dataStr, tariffa: '—', importo: Number(m.importo) || 0, inTotal: false });
            });
        }

        return rows;
    };

    // Righe proforma salvate in datiAggiuntiviJson, per ritrovare la struttura precedente.
    const getSavedProformaRows = (): ProformaRow[] | null => {
        let extra: any = {};
        if (pratica?.datiAggiuntiviJson) {
            try { extra = typeof pratica.datiAggiuntiviJson === 'string' ? JSON.parse(pratica.datiAggiuntiviJson) : pratica.datiAggiuntiviJson; } catch {}
        }
        const saved = extra?.proforma_rows;
        if (!Array.isArray(saved) || saved.length === 0) return null;
        return saved.map((r: any) => ({
            id: String(r.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
            descrizione: String(r.descrizione ?? ''),
            quantita: String(r.quantita ?? ''),
            tariffa: String(r.tariffa ?? ''),
            importo: Number(r.importo) || 0,
            inTotal: !!r.inTotal,
            isSection: !!r.isSection,
            isExtra: !!r.isExtra,
            ...(r.noIva ? { noIva: true } : {}),
        }));
    };

    // Apre l'anteprima ricostruendo sempre le righe con buildProformaRows: la cache può avere
    // un formato vecchio (una riga per scontrino). Le modifiche manuali precedenti si perdono.
    const openProformaPreview = () => {
        if (baseFatturazione <= 0) {
            addToast('Nessun importo da fatturare. Imposta un preventivo o attendi la maturazione del consuntivo.', 'error');
            return;
        }
        setProformaRows(buildProformaRows());
        setProformaMode('completo');
        setNewRowDraft({ descrizione: '', quantita: '', tariffa: '', importo: '' });
        // Opzioni di stampa ai valori iniziali a ogni apertura (come nell'app): una spunta
        // lasciata da un'apertura annullata non deve finire nel PDF successivo.
        setProformaNascondiIva(false);
        setProformaMostraPagamenti(true);
        setProformaPreviewOpen(true);
    };

    // Ricostruisce le righe dalla pratica scartando le modifiche manuali.
    const rebuildProformaRows = () => {
        setProformaRows(buildProformaRows());
        addToast('Righe ricostruite dai dati attuali', 'success');
    };

    // Salva le righe in datiAggiuntiviJson. Gli errori vengono solo loggati
    // per non bloccare l'utente.
    const persistProformaRows = async (rows: ProformaRow[]) => {
        try {
            await PraticaService.patchDatiAggiuntivi(praticaId, { proforma_rows: rows });
        } catch (e) {
            console.warn('[persistProformaRows] errore salvataggio righe:', e);
        }
    };

    // Totali allineati al gestionale: imponibile = righe inTotal; ivaExtra = IVA reale
    // non coperta dal 22% dell'imponibile (rettifiche Solo IVA e righe noIva);
    // totale = imponibile + IVA; saldo = totale − incassato.
    const proformaEsclusiRighe = proformaRows.reduce((s, r) => s + (r.inTotal && r.noIva ? Number(r.importo) || 0 : 0), 0);
    const ivaExtraProforma = Math.round((canali.ivaDovuta - (canali.imponibileDovuto - proformaEsclusiRighe) * (ALIQUOTA_IVA / 100)) * 100) / 100;
    const proformaTotali = calcolaTotaliProforma(proformaRows, ALIQUOTA_IVA, proformaNascondiIva, ivaExtraProforma);
    const proformaTotaleBase = proformaTotali.totaleBase;
    const proformaIva = proformaTotali.iva;
    const proformaTotaleIvato = proformaTotali.totale;
    const proformaTotale = proformaMode === 'saldo' ? Math.max(0, proformaTotaleIvato - totaleIncassi) : proformaTotaleIvato;

    const addProformaRow = () => {
        // Accetta virgola o punto, niente negativi.
        const importoNum = parseDecimal(newRowDraft.importo);
        if (!newRowDraft.descrizione.trim() && importoNum === 0) {
            addToast('Inserisci almeno descrizione o importo', 'error');
            return;
        }
        setProformaRows([...proformaRows, {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            descrizione: newRowDraft.descrizione.trim() || 'Voce aggiuntiva',
            quantita: newRowDraft.quantita.trim() || '—',
            tariffa: newRowDraft.tariffa.trim() || '—',
            importo: importoNum,
            inTotal: true,
            isExtra: true,
        }]);
        setNewRowDraft({ descrizione: '', quantita: '', tariffa: '', importo: '' });
    };

    const removeProformaRow = (id: string) => {
        setProformaRows(proformaRows.filter(r => r.id !== id));
    };

    // Aggiunge o aggiorna nelle rettifiche la voce con il totale delle spese a rivalsa
    // approvate, che finisce poi nella proforma. Idempotente grazie all'id sentinella.
    const SPESE_RIVALSA_VOCE_ID = 'auto-spese-rivalsa';
    const KM_RIVALSA_VOCE_ID = 'auto-km-rivalsa';
    const [addingSpeseToPreventivo, setAddingSpeseToPreventivo] = useState(false);
    const [addingKmToPreventivo, setAddingKmToPreventivo] = useState(false);
    const aggiungiSpeseAlleRettifiche = async () => {
        if (stats.totSpese <= 0) {
            addToast('Nessuna spesa a rivalsa da aggiungere', 'error');
            return;
        }
        const currentAgg = aggiustamentiLocal !== null ? aggiustamentiLocal : aggiustamentiFromPratica;
        const existing = currentAgg.find(a => a.id === SPESE_RIVALSA_VOCE_ID);
        const ok = window.confirm(
            existing
                ? `Aggiornare la rettifica "Spese a rivalsa" col nuovo totale (€ ${stats.totSpese.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})})?\nSarà inclusa nel prossimo proforma generato.`
                : `Aggiungere una rettifica "Spese a rivalsa" (€ ${stats.totSpese.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}) al compenso?\nSarà inclusa nel prossimo proforma generato.`
        );
        if (!ok) return;
        setAddingSpeseToPreventivo(true);
        try {
            // Esclude i buoni pasto, come nel calcolo di totSpese.
            const tipi = Array.from(new Set((reports || [])
                .filter((r: any) => r.stato === 'APPROVATO')
                .flatMap((r: any) => (r.speseEspanso || r.spese || [])
                    .filter((s: any) => !s.buonoPastoAuto)
                    .map((s: any) => s.tipo || ''))
                .filter(Boolean)));
            const descrSuffix = tipi.length > 0 ? ` (${tipi.join(', ').toLowerCase()})` : '';
            const nuovaRettifica: Aggiustamento = {
                id: SPESE_RIVALSA_VOCE_ID,
                descrizione: `Spese a rivalsa${descrSuffix}`,
                importo: Number(stats.totSpese.toFixed(2)),
                ivaMode: defaultModeSpese,
            };
            const next = [...currentAgg];
            const idx = next.findIndex(a => a.id === SPESE_RIVALSA_VOCE_ID);
            if (idx >= 0) next[idx] = nuovaRettifica; else next.push(nuovaRettifica);
            // Come handleSaveAggiustamenti: rettifiche e compenso finale flat si escludono a vicenda.
            await PraticaService.patchDatiAggiuntivi(praticaId, {
                aggiustamenti: next,
                compenso_finale_concordato: 0,
            });
            setAggiustamentiLocal(next);
            onDatiAggiuntiviChange?.({ aggiustamenti: next, compenso_finale_concordato: 0 });
            addToast(idx >= 0 ? 'Rettifica spese aggiornata' : 'Rettifica spese aggiunta', 'success');
        } catch {
            addToast('Errore aggiornamento rettifiche', 'error');
        } finally {
            setAddingSpeseToPreventivo(false);
        }
    };

    // Spese con carta aziendale → rettifica dedicata (idempotente via id sentinella).
    // Non sono mai nella base, quindi nessun doppio conteggio.
    const SPESE_CARTA_VOCE_ID = 'auto-spese-carta-aziendale';
    const [addingSpeseCarta, setAddingSpeseCarta] = useState(false);
    const aggiungiSpeseCartaAlleRettifiche = async () => {
        if (stats.totSpeseCarta <= 0 || addingSpeseCarta) return;
        const currentAgg = aggiustamentiLocal !== null ? aggiustamentiLocal : aggiustamentiFromPratica;
        const existing = currentAgg.find(a => a.id === SPESE_CARTA_VOCE_ID);
        const totStr = stats.totSpeseCarta.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2});
        const ok = window.confirm(
            existing
                ? `Aggiornare la rettifica "Spese anticipate con carta aziendale" col nuovo totale (€ ${totStr})?\nSarà inclusa nel prossimo proforma generato.`
                : `Aggiungere al compenso le spese pagate con la carta aziendale (€ ${totStr})?\nSarà inclusa nel prossimo proforma generato.`
        );
        if (!ok) return;
        setAddingSpeseCarta(true);
        try {
            const nuova: Aggiustamento = {
                id: SPESE_CARTA_VOCE_ID,
                descrizione: 'Spese anticipate con carta aziendale',
                importo: Number(stats.totSpeseCarta.toFixed(2)),
                ivaMode: defaultModeSpese,
            };
            const next = [...currentAgg];
            const idx = next.findIndex(a => a.id === SPESE_CARTA_VOCE_ID);
            if (idx >= 0) next[idx] = nuova; else next.push(nuova);
            await PraticaService.patchDatiAggiuntivi(praticaId, { aggiustamenti: next, compenso_finale_concordato: 0 });
            setAggiustamentiLocal(next);
            onDatiAggiuntiviChange?.({ aggiustamenti: next, compenso_finale_concordato: 0 });
            addToast(idx >= 0 ? 'Rettifica carta aziendale aggiornata' : 'Rettifica carta aziendale aggiunta', 'success');
        } catch {
            addToast('Errore aggiornamento rettifiche', 'error');
        } finally {
            setAddingSpeseCarta(false);
        }
    };

    // Rettifica con il totale km × tariffa, idempotente come quella delle spese.
    const aggiungiKmAlleRettifiche = async () => {
        if (stats.totKm <= 0 || stats.costoKm <= 0) {
            addToast('Nessun km approvato (o tariffa km a zero) da aggiungere', 'error');
            return;
        }
        const currentAgg = aggiustamentiLocal !== null ? aggiustamentiLocal : aggiustamentiFromPratica;
        const existing = currentAgg.find(a => a.id === KM_RIVALSA_VOCE_ID);
        const ok = window.confirm(
            existing
                ? `Aggiornare la rettifica "Indennità chilometrica" col nuovo totale (€ ${stats.costoKm.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})})?\nSarà inclusa nel prossimo proforma generato.`
                : `Aggiungere una rettifica "Indennità chilometrica" (${fmtKm(stats.totKm)} km × € ${fmtIt(tariffaKm)}/km = € ${stats.costoKm.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}) al compenso?\nSarà inclusa nel prossimo proforma generato.`
        );
        if (!ok) return;
        setAddingKmToPreventivo(true);
        try {
            const nuovaRettifica: Aggiustamento = {
                id: KM_RIVALSA_VOCE_ID,
                descrizione: `Indennità chilometrica (${fmtKm(stats.totKm)} km × € ${fmtIt(tariffaKm)}/km)`,
                importo: Number(stats.costoKm.toFixed(2)),
                ivaMode: DEFAULT_RETTIFICA_MODE,
            };
            const next = [...currentAgg];
            const idx = next.findIndex(a => a.id === KM_RIVALSA_VOCE_ID);
            if (idx >= 0) next[idx] = nuovaRettifica; else next.push(nuovaRettifica);
            await PraticaService.patchDatiAggiuntivi(praticaId, {
                aggiustamenti: next,
                compenso_finale_concordato: 0,
            });
            setAggiustamentiLocal(next);
            onDatiAggiuntiviChange?.({ aggiustamenti: next, compenso_finale_concordato: 0 });
            addToast(idx >= 0 ? 'Rettifica km aggiornata' : 'Rettifica km aggiunta', 'success');
        } catch {
            addToast('Errore aggiornamento rettifiche', 'error');
        } finally {
            setAddingKmToPreventivo(false);
        }
    };

    // ── PDF proforma ──
    // Prepara i dati specifici della pratica e delega a generaProformaPdf.
    const generateProformaPdf = async (
        rows: ProformaRow[],
        mode: 'completo' | 'saldo',
        opts: { download: boolean; docNumber?: string } = { download: true },
    ): Promise<{ blob: Blob; filename: string } | null> => {
        const totaleBase = rows.reduce((s, r) => s + (r.inTotal ? Number(r.importo) || 0 : 0), 0);
        if (totaleBase <= 0 && rows.length === 0) {
            addToast('Nessuna riga nel proforma.', 'error');
            return null;
        }
        try {
            const clientRes = pratica?.clientId ? await ClientService.getById(pratica.clientId).catch(() => null) : null;
            const cliente: any = (clientRes as any)?.data || clientRes || {};
            let extra: any = {};
            if (pratica?.datiAggiuntiviJson) {
                try { extra = typeof pratica.datiAggiuntiviJson === 'string' ? JSON.parse(pratica.datiAggiuntiviJson) : pratica.datiAggiuntiviJson; } catch {}
            }

            // docNumber è il numero ufficiale assegnato dal BE; il fallback serve solo a chiamate legacy.
            const oggi = new Date();
            const docNumber = opts.docNumber
                ?? `PF-${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}${String(oggi.getDate()).padStart(2, '0')}-${pratica?.id || ''}`;
            const oggetto: string[] = [`Pratica: ${pratica?.name || `#${pratica?.id || ''}`}`];
            if (extra.rif_numero_pratica) oggetto.push(`Riferimento: ${extra.rif_numero_pratica}`);
            if (extra.data_incarico) oggetto.push(`Data incarico: ${new Date(extra.data_incarico).toLocaleDateString('it-IT')}`);

            // Solo gli incassi registrati: l'acconto pattuito non è un pagamento ricevuto
            // e, una volta incassato, compare già come entrata.
            const pagamenti: Array<{ data: string; descrizione: string; importo: number }> = [];
            movimenti.filter(m => m.tipo === 'entrata').forEach((m: any) => {
                pagamenti.push({
                    data: m.data || '—',
                    descrizione: m.descrizione || 'Pagamento ricevuto',
                    importo: Number(m.importo) || 0,
                });
            });

            const safeName = (pratica?.name || `pratica_${pratica?.id || ''}`).replace(/[^a-zA-Z0-9_-]+/g, '_');
            const result = await generaProformaPdf({
                rows, mode, settings, cliente,
                docNumber, oggetto, safeName,
                aliquotaIva, totaleIncassi, pagamenti,
                nascondiIva: proformaNascondiIva,
                mostraPagamenti: proformaMostraPagamenti,
                ivaExtra: ivaExtraProforma,
            });
            if (!result) {
                addToast('Errore nella generazione della proforma', 'error');
                return null;
            }
            if (opts.download) {
                const url = URL.createObjectURL(result.blob);
                const a = document.createElement('a');
                a.href = url; a.download = result.filename;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
                addToast('Fattura proforma generata', 'success');
            }
            return result;
        } catch (e) {
            console.error('[generateProformaPdf] errore:', e);
            addToast('Errore nella generazione della proforma', 'error');
            return null;
        }
    };

    // Crea il record PROFORMA prima del PDF: il BE assegna il numero (PF-YYYY-NNNN)
    // che viene stampato nel documento, così lista e PDF coincidono.
    // L'hash del contenuto (righe, modalità, opzioni, IVA e, se mostrati, i pagamenti)
    // permette al BE di riusare la proforma esistente invece di consumare un nuovo numero.
    const computeProformaHash = (): string => {
        const showsPayments = proformaMode === 'saldo' || !!proformaMostraPagamenti;
        const sig = JSON.stringify({
            v: 1,
            rows: proformaRows.map(r => ({
                d: (r.descrizione || '').trim(),
                q: (r.quantita || '').trim(),
                t: (r.tariffa || '').trim(),
                i: Number(r.importo) || 0,
                tot: !!r.inTotal,
                sec: !!r.isSection,
                // noIva solo se true, così l'hash delle proforme senza righe esenti non cambia.
                ...(r.noIva ? { ni: 1 } : {}),
            })),
            mode: proformaMode,
            nascondiIva: !!proformaNascondiIva,
            mostraPagamenti: !!proformaMostraPagamenti,
            iva: Number(aliquotaIva) || 0,
            incassi: showsPayments ? (Number(totaleIncassi) || 0) : 0,
            acconto: showsPayments ? (Number(accontoIniziale) || 0) : 0,
            ivaExtra: showsPayments ? (Number(ivaExtraProforma) || 0) : 0,
            ...hashMarkerBaseIva(economiaProforma(proformaRows, ivaExtraProforma)),
        });
        // cyrb53: stesso algoritmo dell'app, così web e app deduplicano allo stesso modo.
        let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
        for (let i = 0; i < sig.length; i++) {
            const ch = sig.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
    };

    const createProformaRecord = async (): Promise<any | null> => {
        try {
            if (!pratica?.clientId) {
                console.warn('[createProformaRecord] pratica senza clientId');
                return null;
            }
            // Quota esente (righe noIva) e IVA extra vanno al BE, che altrimenti calcolerebbe
            // l'IVA su tutto l'imponibile: così il record ha lo stesso totale del PDF.
            const economia = economiaProforma(proformaRows, ivaExtraProforma);
            // Le righe del modale hanno quantità/tariffa come stringhe ('5h', '€12/h'), mentre
            // l'editor preventivi vuole numeri: senza conversione parseFloat('5h') darebbe NaN.
            const vociPreventivo = proformaRows
                .filter(r => !r.isSection)
                .map(r => ({
                    id: r.id,
                    descrizione: r.descrizione.trim() + (r.quantita && r.quantita !== '—' && r.quantita.trim() !== '' ? ` (${r.quantita.trim()}${r.tariffa && r.tariffa !== '—' ? ' × ' + r.tariffa.trim() : ''})` : ''),
                    quantita: 1,
                    unita: 'forfait',
                    prezzoUnitario: Number(r.importo) || 0,
                    scontoPercentuale: 0,
                }));
            const createRes = await PreventiviService.create({
                clientMode: 'existing',
                clientId: pratica.clientId,
                voci: vociPreventivo,
                ...economia,
                ivaPercentuale: aliquotaIva,
                stato: 'INVIATO',
                praticaId,
                tipoDocumento: 'PROFORMA',
                nascondiIva: proformaNascondiIva,
                proformaContentHash: computeProformaHash(),
            });
            const created = createRes?.data;
            if (!created?.id) {
                console.warn('[createProformaRecord] Preventivo creato senza id');
                return null;
            }
            return created;
        } catch (e) {
            console.warn('[createProformaRecord] errore:', e);
            return null;
        }
    };

    // Carica il PDF su S3, aggiorna il record PROFORMA e la lista delle proforma emesse.
    const uploadProformaPdfToRecord = async (created: any, blob: Blob, filename: string): Promise<any | null> => {
        try {
            const uploadRes = await PreventiviService.savePdf(created.id, blob, filename);
            const updated = uploadRes?.data ?? created;
            setPreventiviCollegati(prev => [updated, ...prev.filter(x => x.id !== updated.id)]);
            return updated;
        } catch (e) {
            console.warn('[uploadProformaPdfToRecord] errore upload PDF proforma:', e);
            addToast('Proforma generata ma non archiviata sul mandato. Riprova più tardi.', 'error');
            return null;
        }
    };

    // Ordine: crea il record (numero dal BE), genera il PDF con quel numero, carica e salva
    // le righe. Se la creazione fallisce si genera comunque il PDF senza numero ufficiale.
    const handleSaveProforma = async () => {
        setGeneratingProforma(true);
        try {
            const created = await createProformaRecord();
            const result = await generateProformaPdf(proformaRows, proformaMode, {
                download: true,
                docNumber: created?.numeroProgressivo,
            });
            if (result) {
                await persistProformaRows(proformaRows);
                if (created?.reused) {
                    // Proforma con lo stesso contenuto già esistente: basta assicurarsi che sia in lista.
                    setPreventiviCollegati(prev => [created, ...prev.filter(x => x.id !== created.id)]);
                    addToast(`Proforma ${created.numeroProgressivo || ''} riscaricata (contenuto invariato)`.trim(), 'success');
                } else if (created?.id) {
                    await uploadProformaPdfToRecord(created, result.blob, result.filename);
                }
                setProformaPreviewOpen(false);
            }
        } finally {
            setGeneratingProforma(false);
        }
    };

    // Condivide il PDF con la Web Share API; se non supportata lo scarica.
    // Stesso ordine di handleSaveProforma.
    const handleShareProforma = async () => {
        setIsPreparingShare(true);
        try {
            const created = await createProformaRecord();
            const result = await generateProformaPdf(proformaRows, proformaMode, {
                download: false,
                docNumber: created?.numeroProgressivo,
            });
            if (!result) return;
            const { blob, filename } = result;
            const file = new File([blob], filename, { type: 'application/pdf' });
            const nav = navigator as any;
            if (nav.canShare && nav.canShare({ files: [file] })) {
                try {
                    await nav.share({
                        title: 'Fattura Proforma',
                        text: `Totale ${proformaMode === 'saldo' ? 'saldo da versare' : 'proforma'}: ${formatEur(proformaTotale)}`,
                        files: [file],
                    });
                    await persistProformaRows(proformaRows);
                    if (created?.reused) {
                        setPreventiviCollegati(prev => [created, ...prev.filter(x => x.id !== created.id)]);
                    } else if (created?.id) {
                        await uploadProformaPdfToRecord(created, blob, filename);
                    }
                    setProformaPreviewOpen(false);
                    return;
                } catch (err: any) {
                    // AbortError: l'utente ha chiuso il foglio di condivisione.
                    if (err?.name === 'AbortError') return;
                    throw err;
                }
            }
            // Browser senza supporto: scarica il file.
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
            await persistProformaRows(proformaRows);
            if (created?.reused) {
                setPreventiviCollegati(prev => [created, ...prev.filter(x => x.id !== created.id)]);
            } else if (created?.id) {
                await uploadProformaPdfToRecord(created, blob, filename);
            }
            addToast('Condivisione non supportata dal browser, PDF scaricato.', 'success');
        } catch (e) {
            console.error('[handleShareProforma]', e);
            addToast('Errore nella condivisione del PDF', 'error');
        } finally {
            setIsPreparingShare(false);
        }
    };

    if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={32}/></div>;

    return (
        <div className="space-y-6 animate-in fade-in duration-300 pb-10">

            {/* Avviso report non approvati */}
            {stats.reportDaApprovare > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl flex items-center gap-3 shadow-sm">
                    <AlertTriangle size={18} className="text-amber-500 shrink-0"/>
                    <div className="text-sm font-medium">
                        Sono presenti <strong>{stats.reportDaApprovare} relazioni operative</strong> in attesa di approvazione. Il bilancio sottostante non include le relative spese fino a validazione avvenuta.
                    </div>
                </div>
            )}

            {/* Soglia budget, solo a consuntivo con budget impostato:
               ambra al 90%, rosso al superamento. */}
            {isConsuntivo && preventivo > 0 && stats.totaleConsuntivo > 0 && (() => {
                const perc = (stats.totaleConsuntivo / preventivo) * 100;
                if (perc < 90) return null;
                const superato = perc >= 100;
                return (
                    <div className={`flex items-center justify-between gap-3 p-4 rounded-2xl border shadow-sm ${
                        superato ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${superato ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                                <AlertTriangle size={18}/>
                            </div>
                            <div>
                                <p className={`text-sm font-bold ${superato ? 'text-rose-800' : 'text-amber-800'}`}>
                                    {superato
                                        ? `Budget massimo superato (${perc.toFixed(0)}%)`
                                        : `Budget al ${perc.toFixed(0)}% — Soglia massima quasi raggiunta`}
                                </p>
                                <p className={`text-xs mt-0.5 ${superato ? 'text-rose-700' : 'text-amber-700'}`}>
                                    Consuntivo maturato <strong>€ {stats.totaleConsuntivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                                    {' '}/ budget pattuito <strong>€ {preventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                                    {superato && <> · sforamento <strong>€ {(stats.totaleConsuntivo - preventivo).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong></>}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 1. KPI finanziari */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Base di fatturazione (concordato, preventivo o consuntivo maturato) */}
                <div className={`p-5 rounded-2xl shadow-sm flex flex-col justify-between border ${hasCompensoFinale || hasRettificheEstratto ? 'bg-blue-50 border-blue-100' : 'bg-white border-slate-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                        <span className={`text-xs font-bold uppercase tracking-widest ${hasCompensoFinale || hasRettificheEstratto ? 'text-blue-700' : 'text-slate-500'}`}>
                            {hasAggiustamenti || hasRettificheEstratto ? 'Importo Rettificato' : (hasCompensoFinale ? 'Importo Concordato' : (isConsuntivo ? 'Consuntivo Maturato' : 'Preventivo Accordato'))}
                        </span>
                        <div className={`p-2 rounded-lg ${hasCompensoFinale ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                            {hasCompensoFinale ? <Tag size={16}/> : <Wallet size={16}/>}
                        </div>
                    </div>
                    <div>
                        {/* Numero grande = imponibile; sotto totale e IVA. */}
                        <div className={`text-2xl font-black ${hasCompensoFinale || hasRettificheEstratto ? 'text-blue-700' : 'text-slate-800'}`}>€ {baseFatturazione.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        <div className="text-[10px] font-bold text-slate-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                            <span>Totale € {baseFatturazioneIvata.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            <span>·</span>
                            <span>IVA {aliquotaIva}% € {ivaValore.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="text-xs font-semibold text-slate-400 mt-1">
                            {hasAggiustamenti
                                ? `Base €${baseConsuntivoOPreventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · ${sumAggImponibile >= 0 ? '+' : '−'} €${Math.abs(sumAggImponibile).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} rettifiche (imp.)`
                                : (hasCompensoFinale
                                    ? `Maturato €${baseConsuntivoOPreventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}${baseConsuntivoOPreventivo > compensoFinale ? ` · sconto €${(baseConsuntivoOPreventivo - compensoFinale).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ''}`
                                    : (isConsuntivo
                                        ? (hasRettificheEstratto
                                            ? 'Ore + km + spese con rettifiche estratto conto'
                                            : (preventivo > 0 ? `Budget massimo €${preventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'Ore + km + spese approvate'))
                                        : 'Valore iniziale da mandato'))}
                        </div>
                    </div>
                </div>

                {/* Saldo da incassare */}
                <div className={`p-5 rounded-2xl shadow-sm flex flex-col justify-between relative overflow-hidden border ${isSaldato ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-200'}`}>
                    <div className="flex justify-between items-start mb-2 relative z-10">
                        <span className={`text-xs font-bold uppercase tracking-widest ${isSaldato ? 'text-emerald-800' : 'text-slate-500'}`}>Da Incassare</span>
                        <div className={`p-2 rounded-lg ${isSaldato ? 'bg-emerald-100 text-emerald-600' : 'bg-orange-100 text-orange-500'}`}><TrendingUp size={16}/></div>
                    </div>
                    <div className="relative z-10">
                        {/* Numero grande = residuo imponibile; sotto totale e IVA. */}
                        <div className={`text-2xl font-black ${isSaldato ? 'text-emerald-700' : 'text-slate-800'}`}>
                            € {Math.abs(saldoDaIncassareImponibile).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            {canali.isImponibileSaldato && <span className="text-sm font-semibold ml-1 text-emerald-600">✓</span>}
                        </div>
                        {baseFatturazione > 0 && (
                            <div className="text-[10px] font-bold text-slate-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                <span>Totale € {Math.abs(saldoDaIncassare).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                <span>·</span>
                                <span>IVA {aliquotaIva}% € {Math.abs(saldoDaIncassareIva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                        )}
                        {saldoDaIncassareIva < -0.005 && (
                            <div className="text-[10px] font-bold text-amber-600 mt-0.5">IVA incassata in eccesso: € {Math.abs(saldoDaIncassareIva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        )}
                        {baseFatturazione > 0 ? (
                            <>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full transition-all ${isSaldato ? 'bg-emerald-500' : 'bg-orange-400'}`} style={{width: `${percIncassato}%`}}/>
                                    </div>
                                    <span className={`text-[10px] font-bold ${isSaldato ? 'text-emerald-600' : 'text-slate-400'}`}>
                                        {isSaldato ? 'Saldato' : `${percIncassato.toFixed(0)}% incassato`}
                                    </span>
                                </div>
                                {accontoIniziale > 0 && totaleIncassi < accontoIniziale && (
                                    <div className="text-[10px] font-semibold text-slate-400 mt-1">
                                        di cui acconto pendente: € {fmtIt(accontoIniziale - totaleIncassi)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-xs text-slate-400 mt-1">
                                {isConsuntivo ? 'Nessun consuntivo maturato' : 'Nessun preventivo impostato'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Totale Incassato */}
                <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-2xl shadow-sm flex flex-col justify-between">
                    <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest">Totale Incassato</span>
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg"><Landmark size={16}/></div>
                    </div>
                    <div>
                        {/* Numero grande = imponibile incassato; sotto totale e IVA. */}
                        <div className="text-2xl font-black text-emerald-700">€ {canali.imponibileIncassato.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        {totaleIncassi > 0 && (
                            <div className="text-[10px] font-bold text-emerald-700/70 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                <span>Totale € {totaleIncassi.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                <span>·</span>
                                <span>IVA {aliquotaIva}% € {canali.ivaIncassata.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                        )}
                        <div className="text-xs font-semibold text-emerald-600/70 mt-1">
                            {accontoIniziale > 0
                                ? `Acconto da mandato: € ${fmtIt(accontoIniziale)}`
                                : 'Pagamenti ricevuti'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Rettifiche dell'estratto conto operativo: solo a consuntivo. */}
            {isConsuntivo && (
            <div className={`bg-white border rounded-2xl shadow-sm p-4 ${hasRettificheEstratto ? 'border-blue-200' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${hasRettificheEstratto ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}><Clock size={16}/></div>
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800">Rettifiche Estratto Conto</div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {hasRettificheEstratto
                                    ? VOCI_ESTRATTO.filter(v => Math.abs(sommeEstratto[v]) > 0).map(v => `${VOCE_ESTRATTO_LABEL[v].replace('Rettifica ', '')} ${formatQuantitaEstratto(v, sommeEstratto[v])}`).join(' · ')
                                    : 'Ore, km e spese in più o in meno verso il cliente, senza toccare i report'}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setRettificheEstrattoModalOpen(true)}
                        className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 border transition-colors ${
                            hasRettificheEstratto
                                ? 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-100'
                                : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                    >
                        <Pencil size={12}/> {hasRettificheEstratto ? 'Modifica' : 'Aggiungi rettifica'}
                    </button>
                </div>
            </div>
            )}

            {/* Rettifiche al compenso */}
            <div className={`bg-white border rounded-2xl shadow-sm p-4 ${hasAggiustamenti ? 'border-blue-200' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${hasAggiustamenti ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}><Tag size={16}/></div>
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800">Rettifiche al Compenso</div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {hasAggiustamenti
                                    ? `${aggiustamenti.length} voce${aggiustamenti.length > 1 ? 'i' : ''} · imp. ${sumAggImponibile >= 0 ? '+' : '−'} €${Math.abs(sumAggImponibile).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · IVA ${sumAggIva >= 0 ? '+' : '−'} €${Math.abs(sumAggIva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                    : 'Aggiunte o detrazioni sul compenso base (sconti, extra, ecc.)'}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={openAggiustamentiModal}
                        className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 border transition-colors ${
                            hasAggiustamenti
                                ? 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-100'
                                : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                    >
                        <Pencil size={12}/> {hasAggiustamenti ? 'Modifica' : 'Aggiungi rettifica'}
                    </button>
                </div>

                {hasAggiustamenti && (
                    <div className="mt-3 space-y-1">
                        <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                            <span className="text-xs text-slate-500">Base ({isConsuntivo ? 'consuntivo' : 'preventivo'})</span>
                            <span className="text-xs font-bold text-slate-700">€ {baseConsuntivoOPreventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        {aggiustamenti.map(a => {
                            // Quota sul canale imponibile: "solo IVA" → 0, "IVA inclusa" → scorporata, "senza IVA" → importo.
                            const mode = (a.ivaMode as IvaMode) || DEFAULT_RETTIFICA_MODE;
                            const raw = Number(a.importo) || 0;
                            const neg = raw < 0;
                            const sp = splitRettifica(raw, mode);
                            return (
                            <div key={a.id} className="flex items-center justify-between gap-2 py-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded shrink-0 ${neg ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                        {neg ? '−' : '+'}
                                    </span>
                                    <span className="text-xs text-slate-600 truncate">{a.descrizione || 'Rettifica'}</span>
                                    <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1 py-0.5 rounded shrink-0">{IVA_MODE_SHORT[mode]}</span>
                                </div>
                                <div className="text-right shrink-0">
                                    <span className={`text-xs font-bold ${neg ? 'text-rose-600' : 'text-emerald-600'}`}>
                                        {neg ? '−' : '+'} € {Math.abs(raw).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </span>
                                    <div className="text-[9px] text-slate-400">imp {sp.imp >= 0 ? '+' : '−'}€{Math.abs(sp.imp).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · IVA {sp.iva >= 0 ? '+' : '−'}€{Math.abs(sp.iva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                            </div>
                            );
                        })}
                        <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
                            <span className="text-xs font-bold text-slate-700">Totale rettificato (imponibile)</span>
                            <span className="text-sm font-black text-blue-700">€ {compensoFinale.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                    </div>
                )}

                {!hasAggiustamenti && compensoFinaleFromPratica > 0 && (
                    <div className="mt-3 flex items-center justify-between bg-amber-50 border border-amber-100 rounded-xl p-3 gap-3">
                        <div className="min-w-0">
                            <div className="text-xs font-bold text-amber-700">Importo finale concordato (vecchio formato)</div>
                            <div className="text-xs text-amber-600">€ {compensoFinaleFromPratica.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        </div>
                        <button onClick={openAggiustamentiModal}
                                className="shrink-0 text-[10px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-700 px-2 py-1 rounded-lg border border-amber-200 transition-colors">
                            Converti
                        </button>
                    </div>
                )}
            </div>

            {/* Proforma: riepilogo fatturabile sul lavoro maturato */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600"><FileDown size={18}/></div>
                    <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-800">Fattura Proforma</div>
                        <div className="text-[11px] text-slate-500">
                            Riepilogo per il cliente del lavoro maturato, dei pagamenti ricevuti e del saldo da versare. Non costituisce documento fiscale.
                        </div>
                    </div>
                </div>
                <button
                    onClick={openProformaPreview}
                    disabled={generatingProforma || baseFatturazione <= 0}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold px-5 py-2.5 rounded-xl flex items-center justify-center gap-2 shadow-sm whitespace-nowrap"
                    title={baseFatturazione <= 0 ? 'Imposta un preventivo o attendi la maturazione del consuntivo' : 'Anteprima proforma'}
                >
                    {generatingProforma ? <Loader2 size={16} className="animate-spin"/> : <FileDown size={16}/>}
                    Genera Proforma
                </button>
            </div>

            {/* Anteprima proforma con righe modificabili */}
            {proformaPreviewOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                     onClick={() => !generatingProforma && !isPreparingShare && setProformaPreviewOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Anteprima Fattura Proforma</h3>
                                <p className="text-xs text-slate-500 mt-1">Modifica le righe, poi salva o condividi il PDF.</p>
                            </div>
                            <button onClick={() => setProformaPreviewOpen(false)} disabled={generatingProforma || isPreparingShare}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>

                        {/* Modalità Completo / Saldo */}
                        <div className="px-5 pt-4 shrink-0">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Modalità</div>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setProformaMode('completo')}
                                    className={`p-3 border-2 rounded-xl text-left transition-all ${
                                        proformaMode === 'completo'
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <FileDown size={14} className={proformaMode === 'completo' ? 'text-indigo-600' : 'text-slate-400'}/>
                                        <span className="text-xs font-black text-slate-900">Completo</span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-1">Importo pieno dovuto</div>
                                </button>
                                <button
                                    onClick={() => setProformaMode('saldo')}
                                    className={`p-3 border-2 rounded-xl text-left transition-all ${
                                        proformaMode === 'saldo'
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <TrendingUp size={14} className={proformaMode === 'saldo' ? 'text-orange-600' : 'text-slate-400'}/>
                                        <span className="text-xs font-black text-slate-900">Solo Saldo</span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-1">Residuo al netto incassi</div>
                                </button>
                            </div>
                        </div>

                        {/* Opzioni proforma */}
                        <div className="px-5 pt-3 shrink-0 space-y-2">
                            <label className="flex items-start gap-2 cursor-pointer select-none">
                                <input type="checkbox" checked={proformaNascondiIva} onChange={e => setProformaNascondiIva(e.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-600" />
                                <span className="text-xs text-slate-600 leading-snug">
                                    <span className="font-bold text-slate-800">Non mostrare l'IVA</span> — nel PDF il box totali mostra solo <b>Totale = imponibile</b> (nessuna riga IVA).
                                </span>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer select-none">
                                <input type="checkbox" checked={proformaMostraPagamenti} onChange={e => setProformaMostraPagamenti(e.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-600" />
                                <span className="text-xs text-slate-600 leading-snug">
                                    <span className="font-bold text-slate-800">Mostra pagamenti ricevuti</span> — se disattivato, la proforma omette la sezione pagamenti, saldo e acconto.
                                </span>
                            </label>
                        </div>

                        {/* Tabella righe */}
                        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-2">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                <span>Voci nel documento ({proformaRows.filter(r => !r.isSection).length})</span>
                                <button
                                    onClick={rebuildProformaRows}
                                    className="text-[10px] font-bold text-slate-500 hover:text-indigo-600 flex items-center gap-1 normal-case tracking-normal"
                                    title="Scarta modifiche e ricostruisci dalle voci attuali della pratica"
                                >
                                    <RefreshCw size={11}/> Ricostruisci
                                </button>
                            </div>
                            <div className="border border-slate-200 rounded-xl overflow-hidden">
                                <div className="bg-slate-50 grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    <div className="col-span-6">Descrizione</div>
                                    <div className="col-span-2 text-center">Q.tà</div>
                                    <div className="col-span-2 text-center">Tariffa</div>
                                    <div className="col-span-2 text-right">Importo</div>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {proformaRows.length === 0 ? (
                                        <div className="px-3 py-6 text-center text-xs text-slate-400">Nessuna riga. Aggiungine una sotto.</div>
                                    ) : proformaRows.map(r => (
                                        r.isSection ? (
                                            <div key={r.id} className="bg-slate-50 px-3 py-1.5 flex items-center justify-between group">
                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">{r.descrizione}</span>
                                                <button
                                                    onClick={() => removeProformaRow(r.id)}
                                                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded transition"
                                                    title="Rimuovi sezione"
                                                >
                                                    <X size={12}/>
                                                </button>
                                            </div>
                                        ) : (
                                            <div key={r.id} className={`px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs group hover:bg-slate-50 ${r.isExtra ? 'bg-emerald-50/30' : ''}`}>
                                                <div className="col-span-6 flex items-center gap-2 min-w-0">
                                                    {r.isExtra && <span className="text-[9px] font-black text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">+</span>}
                                                    <span className="text-slate-800 truncate" title={r.descrizione}>{r.descrizione}</span>
                                                </div>
                                                <div className="col-span-2 text-center text-slate-500">{r.quantita || '—'}</div>
                                                <div className="col-span-2 text-center text-slate-500">{r.tariffa || '—'}</div>
                                                <div className="col-span-2 flex items-center justify-end gap-1.5">
                                                    <span className={`font-bold ${r.inTotal ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                                                        {Number(r.importo) < 0 ? '− ' : ''}{formatEur(Math.abs(Number(r.importo) || 0))}
                                                    </span>
                                                    <button
                                                        onClick={() => removeProformaRow(r.id)}
                                                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded transition shrink-0"
                                                        title="Rimuovi riga"
                                                    >
                                                        <Trash2 size={12}/>
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>

                            {/* Aggiungi riga */}
                            <div className="mt-3 p-3 border-2 border-dashed border-emerald-200 rounded-xl bg-emerald-50/30">
                                <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <Plus size={12}/> Aggiungi voce
                                </div>
                                <div className="grid grid-cols-12 gap-2">
                                    <input
                                        type="text"
                                        placeholder="Descrizione"
                                        value={newRowDraft.descrizione}
                                        onChange={e => setNewRowDraft({ ...newRowDraft, descrizione: e.target.value })}
                                        className="col-span-6 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Q.tà"
                                        value={newRowDraft.quantita}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setNewRowDraft(prev => {
                                                const q = parseDecimal(v);
                                                const t = parseDecimal(prev.tariffa);
                                                return {
                                                    ...prev,
                                                    quantita: v,
                                                    importo: q > 0 && t > 0 ? (Math.round(q * t * 100) / 100).toFixed(2) : prev.importo,
                                                };
                                            });
                                        }}
                                        className="col-span-2 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 text-center"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Tariffa"
                                        value={newRowDraft.tariffa}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setNewRowDraft(prev => {
                                                const q = parseDecimal(prev.quantita);
                                                const t = parseDecimal(v);
                                                return {
                                                    ...prev,
                                                    tariffa: v,
                                                    importo: q > 0 && t > 0 ? (Math.round(q * t * 100) / 100).toFixed(2) : prev.importo,
                                                };
                                            });
                                        }}
                                        className="col-span-2 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 text-center"
                                    />
                                    <NumberInput
                                        step="0.01"
                                        placeholder="0.00"
                                        value={newRowDraft.importo}
                                        onChange={e => setNewRowDraft({ ...newRowDraft, importo: e.target.value })}
                                        onKeyDown={e => { if (e.key === 'Enter') addProformaRow(); }}
                                        className="col-span-2 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 text-right"
                                    />
                                </div>
                                <button
                                    onClick={addProformaRow}
                                    className="mt-2 w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1 transition-colors"
                                >
                                    <Plus size={12}/> Aggiungi al proforma
                                </button>
                            </div>
                        </div>

                        {/* Totale */}
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 shrink-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        {proformaMode === 'saldo' ? 'Saldo da versare' : 'Totale proforma'}
                                    </div>
                                    {proformaMode === 'saldo' ? (
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                            Totale {formatEur(proformaTotaleIvato)} − incassato {formatEur(totaleIncassi)}
                                        </div>
                                    ) : !proformaNascondiIva && (
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                            Imponibile {formatEur(proformaTotaleBase)}{proformaTotali.esclusi > 0.005 ? ` (di cui escluse da IVA ${formatEur(proformaTotali.esclusi)})` : ''} · IVA {formatEur(proformaIva)}
                                        </div>
                                    )}
                                </div>
                                <div className={`text-2xl font-black ${proformaMode === 'saldo' ? 'text-orange-700' : 'text-indigo-700'}`}>
                                    {formatEur(proformaTotale)}
                                </div>
                            </div>
                        </div>

                        {/* Bottoni azioni */}
                        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
                            <button
                                onClick={() => setProformaPreviewOpen(false)}
                                disabled={generatingProforma || isPreparingShare}
                                className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleShareProforma}
                                disabled={generatingProforma || isPreparingShare || proformaRows.length === 0}
                                className="px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
                            >
                                {isPreparingShare ? <Loader2 size={14} className="animate-spin"/> : <Share2 size={14}/>}
                                Condividi
                            </button>
                            <button
                                onClick={handleSaveProforma}
                                disabled={generatingProforma || isPreparingShare || proformaRows.length === 0}
                                className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
                            >
                                {generatingProforma ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
                                Salva proforma
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rettificheEstrattoModalOpen && (
                <RettificheEstrattoModal
                    initial={rettificheEstratto}
                    daReport={stats.daReport}
                    saving={savingRettificheEstratto}
                    onClose={() => setRettificheEstrattoModalOpen(false)}
                    onSave={handleSaveRettificheEstratto}
                />
            )}

            {/* Modale rettifiche al compenso */}
            {aggiustamentiModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                     onClick={() => !savingAggiustamenti && setAggiustamentiModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Rettifiche al Compenso</h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    Base: <strong>€ {baseConsuntivoOPreventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                                    {' '}({isConsuntivo ? 'consuntivo maturato' : 'preventivo concordato'})
                                </p>
                            </div>
                            <button onClick={() => setAggiustamentiModalOpen(false)} disabled={savingAggiustamenti}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="p-5 space-y-2.5 max-h-[40vh] overflow-y-auto">
                            {aggiustamentiEdit.length === 0 && (
                                <div className="text-center py-6 text-slate-400 text-sm">Nessuna rettifica. Aggiungila con i pulsanti in basso.</div>
                            )}
                            {aggiustamentiEdit.map((a, i) => {
                                const mode: IvaMode = (a.ivaMode as IvaMode) || DEFAULT_RETTIFICA_MODE;
                                const s = splitRettifica(Number(a.importo) || 0, mode);
                                const setMode = (m: IvaMode) => {
                                    const upd = [...aggiustamentiEdit];
                                    upd[i] = { ...a, ivaMode: m };
                                    setAggiustamentiEdit(upd);
                                };
                                return (
                                <div key={a.id} className="space-y-1 border-b border-slate-50 pb-2">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                const upd = [...aggiustamentiEdit];
                                                upd[i] = { ...a, importo: -a.importo };
                                                setAggiustamentiEdit(upd);
                                            }}
                                            className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-black border transition-colors ${
                                                Number(a.importo) >= 0
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                    : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                            }`}
                                        >
                                            {Number(a.importo) >= 0 ? '+ Extra' : '− Sconto'}
                                        </button>
                                        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden shrink-0">
                                            <span className="px-2 text-xs text-slate-400 bg-slate-50 border-r border-slate-200 py-1.5">€</span>
                                            <NumberInput
                                                min="0"
                                                step="0.01"
                                                value={Math.abs(Number(a.importo)) || ''}
                                                onChange={e => {
                                                    // Accetta virgola o punto; il segno resta quello del record.
                                                    const v = parseDecimal(e.target.value);
                                                    const upd = [...aggiustamentiEdit];
                                                    upd[i] = { ...a, importo: Number(a.importo) < 0 ? -v : v };
                                                    setAggiustamentiEdit(upd);
                                                }}
                                                className="w-24 px-2 py-1.5 text-sm outline-none"
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            value={a.descrizione}
                                            onChange={e => {
                                                const upd = [...aggiustamentiEdit];
                                                upd[i] = { ...a, descrizione: e.target.value };
                                                setAggiustamentiEdit(upd);
                                            }}
                                            placeholder="Descrizione..."
                                            className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100"
                                        />
                                        <button
                                            onClick={() => setAggiustamentiEdit(aggiustamentiEdit.filter((_, j) => j !== i))}
                                            className="shrink-0 p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                        >
                                            <X size={14}/>
                                        </button>
                                    </div>
                                    {/* Modalità IVA della rettifica ("Solo imponibile" = esclusa da IVA). */}
                                    <div className="flex items-center gap-1.5 flex-wrap pl-1">
                                        {(['LORDO', 'NETTO', 'SOLO_IVA', 'SOLO_IMPONIBILE'] as IvaMode[]).map(m => (
                                            <button key={m} onClick={() => setMode(m)}
                                                className={`text-[10px] font-black px-1.5 py-0.5 rounded border transition-colors ${
                                                    mode === m ? 'bg-blue-100 text-blue-700 border-blue-200'
                                                        : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                                                {IVA_MODE_LABEL[m]}
                                            </button>
                                        ))}
                                        <span className="text-[10px] text-slate-400 ml-1">
                                            imp {s.imp >= 0 ? '+' : '−'} €{Math.abs(s.imp).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            {' · '}IVA {s.iva >= 0 ? '+' : '−'} €{Math.abs(s.iva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                        <div className="px-5 pb-3 flex gap-2">
                            <button
                                onClick={() => setAggiustamentiEdit([...aggiustamentiEdit, { id: `${Date.now()}-${Math.random()}`, descrizione: '', importo: 0, ivaMode: DEFAULT_RETTIFICA_MODE }])}
                                className="flex-1 border-2 border-dashed border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors"
                            >
                                <PlusCircle size={13}/> Aggiungi extra
                            </button>
                            <button
                                onClick={() => setAggiustamentiEdit([...aggiustamentiEdit, { id: `${Date.now()}-${Math.random()}`, descrizione: '', importo: -0.01, ivaMode: DEFAULT_RETTIFICA_MODE }])}
                                className="flex-1 border-2 border-dashed border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors"
                            >
                                <PlusCircle size={13}/> Aggiungi sconto
                            </button>
                        </div>

                        {aggiustamentiEdit.length > 0 && (
                            <div className="mx-5 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-500">
                                    Base €{baseConsuntivoOPreventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    {' '}{aggiustamentiEdit.reduce((s, a) => s + Number(a.importo || 0), 0) >= 0 ? '+' : ''}€{aggiustamentiEdit.reduce((s, a) => s + Number(a.importo || 0), 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                                <span className="text-sm font-black text-slate-900 shrink-0">
                                    = €{(baseConsuntivoOPreventivo + aggiustamentiEdit.reduce((s, a) => s + Number(a.importo || 0), 0)).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                            </div>
                        )}

                        <div className="p-5 pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                            {aggiustamentiEdit.length > 0 ? (
                                <button
                                    onClick={() => setAggiustamentiEdit([])}
                                    disabled={savingAggiustamenti}
                                    className="text-xs text-slate-400 hover:text-rose-500 flex items-center gap-1 disabled:opacity-50"
                                >
                                    <Trash2 size={12}/> Rimuovi tutte
                                </button>
                            ) : <div/>}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setAggiustamentiModalOpen(false)}
                                    disabled={savingAggiustamenti}
                                    className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleSaveAggiustamenti}
                                    disabled={savingAggiustamenti}
                                    className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
                                >
                                    {savingAggiustamenti ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                                    Salva rettifiche
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={`grid grid-cols-1 ${isConsulenzaOBonifica || !isConsuntivo ? '' : 'lg:grid-cols-3'} gap-6`}>

                {/* 2. Dettaglio costi operativi, solo a consuntivo: a corpo il compenso è fisso
                   e le tariffe non si applicano. */}
                {!isConsulenzaOBonifica && isConsuntivo && (
                <div className="lg:col-span-2 space-y-4">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest flex items-center gap-2">
                        Estratto Conto Operativo
                    </h3>

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            <tr>
                                <th className="p-4">Voce di Costo</th>
                                <th className="p-4 text-center">Qtà Rilevata</th>
                                <th className="p-4 text-center">Tariffa Applicata</th>
                                <th className="p-4 text-right">Totale Maturato</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm">
                            {/* Ore feriali */}
                            <tr className="hover:bg-slate-50 transition-colors">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Clock size={16}/></div>
                                        <span className="font-semibold text-slate-800">Ore Feriali</span>
                                    </div>
                                </td>
                                <td className="p-4 text-center font-medium text-slate-600">{fmtIt(stats.totOreFeriali, 1)} h{dettaglioRettifica('ORE_FERIALI')}</td>
                                <td className="p-4 text-center font-medium text-slate-600">€ {fmtIt(tariffaOraria)}/h</td>
                                <td className="p-4 text-right font-bold text-slate-900">€ {stats.costoOreFeriali.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            {/* Ore festive / notturne */}
                            {(stats.totOreFestivaNotturna > 0 || tariffaFestivaNotturna > 0) && (
                                <tr className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Moon size={16}/></div>
                                            <span className="font-semibold text-slate-800">Ore Festive / Notturne</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-center font-medium text-slate-600">{fmtIt(stats.totOreFestivaNotturna, 1)} h{dettaglioRettifica('ORE_FESTIVE')}</td>
                                    <td className="p-4 text-center font-medium text-slate-600">€ {fmtIt(tariffaFestivaNotturna)}/h</td>
                                    <td className="p-4 text-right font-bold text-slate-900">€ {stats.costoOreFestivaNotturna.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                </tr>
                            )}
                            {/* Tariffa giornaliera */}
                            {tariffaGiornaliera > 0 && (
                                <tr className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><CalendarDays size={16}/></div>
                                            <span className="font-semibold text-slate-800">Tariffa Giornaliera</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-center font-medium text-slate-600">{stats.giorniLavorati} g</td>
                                    <td className="p-4 text-center font-medium text-slate-600">€ {fmtIt(tariffaGiornaliera)}/g</td>
                                    <td className="p-4 text-right font-bold text-slate-900">€ {stats.costoGiornaliera.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                </tr>
                            )}
                            {/* Km */}
                            <tr className="hover:bg-slate-50 transition-colors">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Car size={16}/></div>
                                        <span className="font-semibold text-slate-800">Indennità Chilometrica</span>
                                    </div>
                                </td>
                                <td className="p-4 text-center font-medium text-slate-600">{stats.totKm.toLocaleString('it-IT', {maximumFractionDigits: 2})} km{dettaglioRettifica('KM')}</td>
                                <td className="p-4 text-center font-medium text-slate-600">€ {fmtIt(tariffaKm)}/km</td>
                                <td className="p-4 text-right font-bold text-slate-900">€ {stats.costoKm.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            {/* Spese da scontrino */}
                            <tr className="hover:bg-slate-50 transition-colors">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Receipt size={16}/></div>
                                        <span className="font-semibold text-slate-800">Rimborsi Spese Varie</span>
                                    </div>
                                </td>
                                <td className="p-4 text-center font-medium text-slate-600">{Math.abs(sommeEstratto.SPESE) > 0 ? dettaglioRettifica('SPESE') : '-'}</td>
                                <td className="p-4 text-center font-medium text-slate-400 text-xs">Da scontrini</td>
                                <td className="p-4 text-right font-bold text-slate-900">€ {stats.totSpese.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            {/* Buoni pasto: solo informativo, mai addebitati al cliente né nel consuntivo. */}
                            {stats.totBuoniPasto > 0 && (
                            <tr className="bg-slate-50/60">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Receipt size={16}/></div>
                                        <div>
                                            <span className="font-semibold text-slate-700">Buoni pasto</span>
                                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">non addebitati al cliente</span>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4 text-center font-medium text-slate-400">-</td>
                                <td className="p-4 text-center font-medium text-slate-400 text-xs">benefit collaboratore</td>
                                <td className="p-4 text-right font-semibold text-slate-500">€ {stats.totBuoniPasto.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            )}
                            {/* Spese con carta aziendale: fuori dal consuntivo, addebitabili come rettifica. */}
                            {stats.totSpeseCarta > 0 && (
                            <tr className="bg-slate-50/60">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-violet-50 text-violet-600 rounded-lg"><Receipt size={16}/></div>
                                        <div>
                                            <span className="font-semibold text-slate-700">Spese con carta aziendale</span>
                                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">non incluse</span>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4 text-center font-medium text-slate-400">-</td>
                                <td className="p-4 text-center">
                                    <button
                                        onClick={aggiungiSpeseCartaAlleRettifiche}
                                        disabled={addingSpeseCarta}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors disabled:bg-slate-300"
                                        title="Aggiungi/aggiorna queste spese come rettifica al compenso (finirà nel prossimo proforma)"
                                    >
                                        {addingSpeseCarta ? <Loader2 size={11} className="animate-spin"/> : <PlusCircle size={11}/>}
                                        Aggiungi alle rettifiche
                                    </button>
                                </td>
                                <td className="p-4 text-right font-semibold text-slate-500">€ {stats.totSpeseCarta.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            )}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200">
                            <tr>
                                <td colSpan={3} className="p-4 text-right text-xs font-bold text-slate-500 uppercase tracking-widest">Totale Consuntivo Generato</td>
                                <td className="p-4 text-right font-black text-slate-900 text-lg">€ {stats.totaleConsuntivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                    {speseEscluseIvaToggle}
                </div>
                )}

                {/* Mandati a corpo (incluse bonifica e consulenza): solo compenso pattuito
                   e spese a rivalsa maturate, niente tariffe. */}
                {!isConsuntivo && (
                <div className="lg:col-span-2 space-y-4">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Compenso a Corpo</h3>
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
                        <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Compenso pattuito (preventivato)</p>
                                <p className="text-2xl font-black text-slate-900 mt-1">
                                    € {preventivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    <span className="text-xs font-medium text-slate-400 ml-1">imponibile</span>
                                </p>
                                {preventivo > 0 && (
                                    <p className="text-sm font-bold text-slate-700 mt-0.5">
                                        € {(Math.round(preventivo * (1 + aliquotaIva / 100) * 100) / 100).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        <span className="text-xs font-medium text-slate-500 ml-1">totale con IVA {aliquotaIva}%</span>
                                    </p>
                                )}
                                <p className="text-xs text-slate-500 mt-2">
                                    Il mandato è a corpo: il compenso è un valore fisso pattuito col cliente.
                                    Le ore lavorate, le tariffe orarie e i km non concorrono al totale.
                                </p>
                            </div>
                            {stats.totSpese > 0 && (
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Spese a rivalsa</p>
                                    <p className="text-lg font-black text-amber-700 mt-1">€ {stats.totSpese.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-[10px] text-slate-400">scontrini approvati</p>
                                    <button
                                        onClick={aggiungiSpeseAlleRettifiche}
                                        disabled={addingSpeseToPreventivo}
                                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors disabled:bg-slate-300"
                                        title="Aggiungi/aggiorna queste spese come rettifica al compenso (finirà nel prossimo proforma)"
                                    >
                                        {addingSpeseToPreventivo ? <Loader2 size={11} className="animate-spin"/> : <PlusCircle size={11}/>}
                                        Aggiungi alle rettifiche
                                    </button>
                                </div>
                            )}
                            {stats.totKm > 0 && tariffaKm > 0 && (
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Indennità km</p>
                                    <p className="text-lg font-black text-indigo-700 mt-1">€ {stats.costoKm.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-[10px] text-slate-400">{fmtKm(stats.totKm)} km × € {fmtIt(tariffaKm)}/km</p>
                                    <button
                                        onClick={aggiungiKmAlleRettifiche}
                                        disabled={addingKmToPreventivo}
                                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors disabled:bg-slate-300"
                                        title="Aggiungi/aggiorna i km come rettifica al compenso (finirà nel prossimo proforma)"
                                    >
                                        {addingKmToPreventivo ? <Loader2 size={11} className="animate-spin"/> : <PlusCircle size={11}/>}
                                        Aggiungi alle rettifiche
                                    </button>
                                </div>
                            )}
                            {stats.totSpeseCarta > 0 && (
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">Carta aziendale</p>
                                    <p className="text-lg font-black text-violet-700 mt-1">€ {stats.totSpeseCarta.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-[10px] text-slate-400">spese pagate dall'agenzia</p>
                                    <button
                                        onClick={aggiungiSpeseCartaAlleRettifiche}
                                        disabled={addingSpeseCarta}
                                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors disabled:bg-slate-300"
                                        title="Aggiungi/aggiorna queste spese come rettifica al compenso (finirà nel prossimo proforma)"
                                    >
                                        {addingSpeseCarta ? <Loader2 size={11} className="animate-spin"/> : <PlusCircle size={11}/>}
                                        Aggiungi alle rettifiche
                                    </button>
                                </div>
                            )}
                            {/* Buoni pasto: solo informativo, nessun addebito al cliente. */}
                            {stats.totBuoniPasto > 0 && (
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Buoni pasto</p>
                                    <p className="text-lg font-black text-emerald-700 mt-1">€ {stats.totBuoniPasto.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-[10px] text-slate-400">benefit collaboratore — non addebitati al cliente</p>
                                </div>
                            )}
                        </div>
                        {speseEscluseIvaToggle}
                    </div>
                </div>
                )}

                {/* 3. Registro transazioni (entrate/uscite extra) */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Movimentazione</h3>
                        <button onClick={onOpenPayment} className="text-[10px] font-bold bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm">
                            <PlusCircle size={14}/> Nuovo Record
                        </button>
                    </div>

                    {/* Preventivi collegati al mandato (scelti nel wizard o dai pendenti qui sotto).
                       La X scollega senza eliminare. Preventivi e proforma emesse sono in sezioni separate;
                       la spunta "fatturato" vive sui movimenti, non sulle proforma. */}
                    {(() => {
                        const proformaList = preventiviCollegati.filter((p: any) => p.tipoDocumento === 'PROFORMA');
                        const preventivoList = preventiviCollegati.filter((p: any) => p.tipoDocumento !== 'PROFORMA');
                        return (
                            <>
                                {preventivoList.length > 0 && (
                                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-3">
                                        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
                                            <FileText size={13} className="text-indigo-500"/>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Preventivi correlati</span>
                                            <span className="text-[10px] font-bold text-slate-400">({preventivoList.length})</span>
                                        </div>
                                        <ul className="divide-y divide-slate-100">
                                            {preventivoList.map((p: any) => (
                                                <li key={p.id}
                                                    onClick={() => navigate(`/preventivi/${p.id}`)}
                                                    className="px-4 py-2 hover:bg-indigo-50/50 cursor-pointer flex items-center justify-between gap-3 transition-colors">
                                                    {/* Due righe per non sovrapporre numero/stato all'importo: numero e stato sopra, data sotto. */}
                                                    <div className="flex flex-col min-w-0 gap-0.5">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-xs font-bold text-indigo-700">#{p.numeroProgressivo || p.id}</span>
                                                            {p.stato && (
                                                                <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">{p.stato}</span>
                                                            )}
                                                        </div>
                                                        <span className="text-[10px] text-slate-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                        <button
                                                            onClick={e => { e.stopPropagation(); unlinkPreventivo(p.id); }}
                                                            title="Scollega dal mandato (resta disponibile per altri)"
                                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        >
                                                            <X size={13}/>
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {proformaList.length > 0 && (
                                    <div className="bg-white border border-blue-200 rounded-2xl shadow-sm overflow-hidden mb-3">
                                        <div className="px-4 py-2.5 border-b border-blue-100 flex items-center gap-2 bg-blue-50/60">
                                            <Receipt size={13} className="text-blue-600"/>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Proforma emesse</span>
                                            <span className="text-[10px] font-bold text-blue-400">({proformaList.length})</span>
                                        </div>
                                        <ul className="divide-y divide-blue-100">
                                            {proformaList.map((p: any) => (
                                                <li key={p.id} className="px-4 py-2.5 hover:bg-blue-50/30 flex items-center justify-between gap-3 flex-wrap">
                                                    {/* Layout a due righe come sopra. */}
                                                    <div className="flex flex-col min-w-0 gap-0.5 cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)}>
                                                        <span className="text-xs font-bold text-blue-700">#{p.numeroProgressivo || p.id}</span>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[10px] text-slate-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                                            {p.pdfUrl && (
                                                                <a href={p.pdfUrl} target="_blank" rel="noreferrer"
                                                                   onClick={e => e.stopPropagation()}
                                                                   className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1">
                                                                    <Download size={11}/> PDF
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                        <button
                                                            onClick={e => { e.stopPropagation(); deleteProforma(p.id); }}
                                                            title="Elimina questa proforma"
                                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        >
                                                            <Trash2 size={13}/>
                                                        </button>
                                                        <button
                                                            onClick={e => { e.stopPropagation(); unlinkPreventivo(p.id); }}
                                                            title="Scollega dal mandato (resta disponibile per altri)"
                                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        >
                                                            <X size={13}/>
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Pendenti dello stesso cliente non ancora collegati; "Collega" aggiorna entrambe le liste. */}
                                {preventiviPendenti.length > 0 && (
                                    <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden mb-3">
                                        <div className="px-4 py-2.5 border-b border-amber-100 flex items-center gap-2 bg-amber-50/60">
                                            <Plus size={13} className="text-amber-600"/>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Pendenti del cliente (non collegati)</span>
                                            <span className="text-[10px] font-bold text-amber-400">({preventiviPendenti.length})</span>
                                        </div>
                                        <ul className="divide-y divide-amber-100">
                                            {preventiviPendenti.map((p: any) => {
                                                const isProforma = p.tipoDocumento === 'PROFORMA';
                                                return (
                                                    <li key={p.id} className="px-4 py-2 hover:bg-amber-50/40 flex items-center justify-between gap-3 transition-colors">
                                                        {/* Layout a due righe come sopra. */}
                                                        <div className="flex flex-col min-w-0 gap-0.5 cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)}>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-xs font-bold text-amber-800">#{p.numeroProgressivo || p.id}</span>
                                                                <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                                    isProforma ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                                                                }`}>{isProforma ? 'PROFORMA' : 'PREVENTIVO'}</span>
                                                            </div>
                                                            <span className="text-[10px] text-slate-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                                        </div>
                                                        {/* Importo sopra, "Collega" sotto, per non sovrapporsi al badge con importi lunghi. */}
                                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                                            <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                            <button
                                                                onClick={() => linkPreventivo(p.id)}
                                                                title="Collega al mandato corrente"
                                                                className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-600 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors"
                                                            >
                                                                <Plus size={11}/> Collega
                                                            </button>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden h-[300px] overflow-y-auto">
                        {movimenti.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-2 p-6 text-center">
                                <Wallet size={32} className="opacity-50"/>
                                <span className="text-sm font-medium">Nessuna transazione extra registrata.</span>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {movimenti.map((mov) => (
                                    <div key={mov.id} className="p-3 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={`p-2 rounded-full border shrink-0 ${mov.tipo === 'entrata' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                                                {mov.tipo === 'entrata' ? <ArrowDownRight size={14}/> : <ArrowUpRight size={14}/>}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-slate-800 truncate">{mov.descrizione || '—'}</div>
                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{new Date(mov.data || Date.now()).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                        {/* Importo ed elimina sopra, "Fatturato" sotto, allineati a destra. */}
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <div className="flex items-center gap-2">
                                                <div className={`text-sm font-black ${mov.tipo === 'entrata' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                    {mov.tipo === 'entrata' ? '+' : '-'} €{Number(mov.importo).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                </div>
                                                {onDeleteMovimento && (
                                                    <button
                                                        onClick={() => onDeleteMovimento(mov.id)}
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-500 p-1 rounded"
                                                        title="Elimina"
                                                    >
                                                        <Trash2 size={13}/>
                                                    </button>
                                                )}
                                            </div>
                                            {/* Spunta "Fatturato" solo sulle entrate. */}
                                            {mov.tipo === 'entrata' && onToggleFatturato && (
                                                <label
                                                    className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider cursor-pointer transition-colors border ${
                                                        mov.fatturato
                                                            ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                                                            : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                                                    }`}
                                                    title={mov.fatturato
                                                        ? 'Fattura emessa — clicca per togliere'
                                                        : 'Spunta se hai emesso la fattura per questo movimento'}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="w-3 h-3 rounded cursor-pointer accent-blue-600"
                                                        checked={!!mov.fatturato}
                                                        onChange={e => onToggleFatturato(mov.id, e.target.checked)}
                                                    />
                                                    Fatturato
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* Note libere della contabilità, con autosave e indicatore di stato. */}
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                        <FileText size={16} className="text-blue-500"/>
                        Note Contabilità
                    </h3>
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            savingNote === 'saving' ? 'text-blue-500' :
                            savingNote === 'saved' ? 'text-emerald-600' :
                            savingNote === 'error' ? 'text-rose-600' : 'text-slate-300'
                        }`}
                    >
                        {savingNote === 'saving' ? '' :
                         savingNote === 'saved' ? 'Salvato' :
                         savingNote === 'error' ? 'Errore salvataggio' : ''}
                    </span>
                </div>
                <textarea
                    value={noteContabilita}
                    onChange={e => setNoteContabilita(e.target.value)}
                    placeholder="Annotazioni libere su pagamenti, scadenze, accordi verbali, deroghe…"
                    className="w-full min-h-[120px] p-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                />
                <p className="text-[10px] text-slate-400 italic mt-1.5">
                    Salvataggio automatico dopo qualche secondo di pausa.
                </p>
            </div>
        </div>
    );
};